import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CalculationConfig,
  coerceOpenQuoteBoolean,
  FinancialProductEnum,
  KEYS_TO_COMPARE_FOR_UPDATES,
  productTypeEnum,
  TaxType,
} from '../../model/open-quote.model';
import { BaseComponent } from '@app/components/base/base.component';
import { ActivatedRoute } from '@angular/router';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { CalculateDiroComponent } from '../calculate-diro/desktop/calculate-diro.component';
import { environment } from '@env/environment.local';
import { Client } from '@stomp/stompjs';
import { ModalService } from '@app/components/modal/modal.service';

import { TranslateService } from '@ngx-translate/core';
import { Constants } from '@app/constants/Constants';
import { AuthService } from '@app/common/auth.service';
import { OpenQuoteValidations } from './open-quote.validations';

import { quoteEnum } from '@components/grace-special-payments/quote-enum.model';
import { SubBrandService } from '@app/pages/quote/standard-quote/catalogs/sub-brand/sub-brand.service';
import { BrandService } from '@app/pages/quote/standard-quote/catalogs/brand/brand.service';
import { FileType } from '@app/models/fileTypes.enum';
import { vehicles } from 'assets/files/vehicles';
import {
  additionalCollateralTypeOptions,
  classificationOptions,
  controlsInsuranceUpdateArray,
  controlWatchingArray,
  residualValueTypeOptions,
  subsidyTypeOptions,
  taxFreeOptionsNew,
  taxTypeBalloonOptions,
  taxTypeDownPaymentOptions,
  taxTypeWarrantyOptions,
} from '@app/pages/quote/open-quote/deals/deal-form/model';
import { BehaviorSubject, finalize, interval, pairwise, startWith, take, forkJoin, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteType } from '@app/pages/quote/insurance/insurance-model';
import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  InsuranceService,
  InsuranceTypeEnum,
  isLifeInsuranceEligible,
  resolveLifeInsurancePremium,
} from '@app/services/insurance.service';
import { DataSelectIdDirective } from '@app/directives/dealer.directive';
import { DiroIntegrationService } from '@app/pages/quote/open-quote/services/diro-integration.service';
import { OpenQuoteService } from '@app/pages/quote/open-quote/services/open-quote-service.service';
import { FinancialCalculatorService, OpenQuoteHighlightFields } from '@app/pages/quote/open-quote/services/financial-calculator.service';
import { OpenQuoteValidationsService } from '@app/pages/quote/open-quote/services/open-quote-validations.service';
import { createLoadingTracker } from '@app/core/util/loading-tracker';

@Component({
  template: '',
})
export class DealsFormOpenQuoteWrapper extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Servicios
  private readonly fb = inject(FormBuilder);
  private readonly quoteService = inject(OpenQuoteService);
  private readonly modalService = inject(ModalService);
  private readonly authService = inject(AuthService);
  private readonly subBrandService = inject(SubBrandService);
  private readonly brandService = inject(BrandService);
  private readonly translateService = inject(TranslateService);
  protected readonly insuranceService = inject(InsuranceService);
  protected readonly diroService = inject(DiroIntegrationService);
  public readonly financialCalculator = inject(FinancialCalculatorService);
  public readonly validationsService = inject(OpenQuoteValidationsService);
  public readonly ConstantsComp = Constants;
  readonly featureLoyaltyFeriaQuotation = environment.featureLoyaltyFeriaQuotation;

  // INPUTS
  @Input() set deal(value: any) {
    this.lockUpdate = true;
    if (this._deal) {
      // Actualiza objetos dentro de la propuesta luego de que se haya creado
      // Si agrega un nuevo modelo/estructura a la propuesta, se debe agregar el objeto en el dto
      let dto = {
        id: value?.id ? value?.id : null,
        creationDate: value?.creationDate || null,
        profitDeal: value?.profitDeal || null,
        specialPayments: value?.specialPayments || null,
        payments: value?.payments || null,
        grace: value?.grace || null,
        irregularPayments: value?.irregularPayments || null,
        insuranceDeals: value?.insuranceDeals || null,
        insurance: value?.insurance || null,
        result: value?.result,
        dealIsLoyalty:
          coerceOpenQuoteBoolean(value?.dealIsLoyalty) ||
          coerceOpenQuoteBoolean(value?.loyaltyFinancialPackage),
        openQuoteModel: value?.openQuoteModel,
        openQuoteBrand: value?.openQuoteBrand,
        openQuoteSubBrand: value?.openQuoteSubBrand,
      };
      this.formGroup.patchValue(dto);
      this.updateInsuranceData();
      this.diroService.idQuoteDiro = this._deal.idQuote && this._deal.idQuote != null ? this._deal.idQuote : null;
      this.diroService.idDealDiro = this._deal.IdDeal && this._deal.IdDeal != null ? this._deal.IdDeal : null;
    } else {
      this.isStarting.set(true);
      // Monto fijo: el draft persiste purchaseTotal Con IVA (API);
      // el input del form es Sin IVA. Se normaliza ANTES de _deal para
      // que el patch de _catalogTrigger reciba el mismo valor.
      const dealForForm = this.financialCalculator.purchaseFixedAmountDraftToForm(
        value,
        this.ctr['tax'].value
      );
      this._deal = dealForForm;
      this._originalSubBrand = this._deal?.subBrandId;
      this._catalogTrigger.next(this._deal);
      this.formGroup.patchValue(dealForForm);
      if (this.featureLoyaltyFeriaQuotation && value) {
        this.formGroup.patchValue(
          {
            dealIsLoyalty:
              coerceOpenQuoteBoolean(value?.dealIsLoyalty) ||
              coerceOpenQuoteBoolean(value?.loyaltyFinancialPackage),
          },
          { emitEvent: false },
        );
      }
    }

    this.setInitValidatorsForFinancialProduct(false);
    this.onlyCalculateDifferenceFinancialAmount();
    this.onlyCalculateDifferenceOpeningFee();
    this.onlyCalculateDifferenceSubsidy();

    this.calculatePurchaseOption('purchaseTotalWithTaxPrecalc');
    this.calculateSubsidyOptions('subsidyTotalWithTaxPrecalc');
    this.calculateResidualValueOption('residualValueTotalPrecalc');
    this.calculateBalloonOptions('balloonTotalWithTaxPrecalc');
    this.formGroup.updateValueAndValidity();
    this.quantityForPayments = value?.quantity || null;

    if (this._deal && this._deal.profitDeal) {
      this.diroService.idQuoteDiro = this._deal.profitDeal.idQuote;
      this.diroService.idDealDiro = this._deal.profitDeal.idDeal;
    }
    this.lockUpdate = false;
    this.getTerm();
    this.syncLoyaltyControlWithFeriaCustomer();
  }
  @Input() customer: any;
  @Input() quoteType: quoteEnum;
  @Input() financialService: any;
  @Input() idQuote: number;
  @Input() indexDeal: any;
  nameCompany = input('');

  // Outputs
  @Output() requestOpenModal: EventEmitter<void> = new EventEmitter();
  @Output() update: EventEmitter<any> = new EventEmitter();
  @Output() closeEdition: EventEmitter<any> = new EventEmitter();

  // View Childs
  readonly modalConfirmation = viewChild<TemplateRef<any>>('modalConfirmation');
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @ViewChild('specialPaymentsTemplate') specialPayments!: TemplateRef<any>;
  @ViewChild(CalculateDiroComponent)
  calculateDiroComponent: CalculateDiroComponent;
  modalDiroLogin = viewChild<TemplateRef<any>>('modalDiroLogin');
  modalStatus = viewChild<TemplateRef<any>>('modalStatus');
  @ViewChildren(DataSelectIdDirective) controlElements: QueryList<DataSelectIdDirective>;

  // Estados del componente
  private _deal: any;
  formGroup: FormGroup;
  previousFormGroup: Record<string, any>;
  lockUpdate: boolean = false;
  body: any;

  quantity: number = 0;
  currency: any | undefined = 'MXN';

  payments: any[] = [];
  quote: any[] = [];
  index: number;
  loading = createLoadingTracker();
  isLoading = computed(() => this.loading.count() > 0);
  taxType = TaxType;
  collapsed: boolean = true;
  quantityForPayments: number;
  activarBotonJson =
    environment.downloadJson || this.authService.checkRoleFromParentMenu('cotizador0', 'ADMINISTRATOR');
  private client: Client;
  term: string = '';
  termMin: number = 0;
  showSection: number = 1;
  deals: any[] = [];
  _quote: any;
  brands: any[] = [];
  subBrands: any[] = [];
  accessorySubBrands: any[] = [];
  models: any;
  imageSB: any;
  showImg: boolean = false;
  _productType: string = '';
  showImages: {
    hasSubbrandImg: boolean;
    subBrandId: number | null;
    productType: string;
  } = {
      hasSubbrandImg: false,
      subBrandId: 0,
      productType: '',
    };
  isActiveBallon = environment.ACTIVE_PRODUCT_BALLON;
  isActiveCapitalLoan = environment.ACTIVE_PRODUCT_CAPITAL_LOAN;
  isActiveRealEstate = environment.ACTIVE_PRODUCT_REAL_STATE;
  isActivePaymentFixed = environment.active_payment_fixed;
  isActiveAccesoriesMarsh = environment.active_accesories_marsh;
  isActiveCalculateV2 = environment.activate_calculate_v2;

  _selectedModel: any;
  _catalogTrigger = new BehaviorSubject<any | null>(null);
  _focusListeners: any[] = [];
  _focusElement: any;
  _fieldName: any;
  _focusTimeout: any;
  _originalSubBrand: any;

  reCalculateInsuranceDealPayments = false;
  public readonly quoteComponentType = QuoteType.COMERCIAL;
  readonly hasFinancialAmountDifferentFromAPI = signal(false);
  readonly hasOpeningFeeDifferentFromAPI = signal(false);
  readonly hasDownPaymentDifferentFromAPI = signal(false);
  readonly hasSubsidyDifferentFromAPI = signal(false);
  readonly hasPurchaseFixedAmountDifferentFromAPI = signal(false);
  protected readonly taxTypeWarrantyOptions = taxTypeWarrantyOptions;
  protected readonly taxTypeBalloonOptions = taxTypeBalloonOptions;
  protected readonly taxTypeDownPaymentOptions = taxTypeDownPaymentOptions;
  protected readonly additionalCollateralTypeOptions = additionalCollateralTypeOptions;
  protected readonly subsidyTypeOptions = subsidyTypeOptions;
  protected readonly classificationOptions = classificationOptions;
  protected readonly residualValueTypeOptions = residualValueTypeOptions;
  protected readonly taxFreeOptions = taxFreeOptionsNew;

  // Objeto para controlar qué campos deben ser resaltados basándose en la respuesta de la API
  highlightFields: OpenQuoteHighlightFields = this.financialCalculator.emptyHighlightFields;

  public readonly allModels = signal<any[]>([]);
  public readonly isStarting = signal(true);
  public readonly isInsuranceChanged = signal(false);
  public readonly _years = signal('');
  public readonly _filteredData = signal<any>({ year: '', productType: '', classification: true });
  public readonly confirmationData = signal<any>({ title: '', subtitle: '', btn1: '', btn2: '', type: "" });

  public readonly financialProductList = computed(() =>
    this.financialCalculator.financialProductsList.filter((product) => this.validationsService.filterFinancialProduct(product, this.nameCompany()))
  );
  // Se calcula el listado de años por producto
  public readonly yearsListByProduct = computed(() => {
    const data = this._filteredData();
    const yearsMap = new Set(
      this.allModels()
        .filter((i) => i.modelType == data.productType && i.enabled && (data.classification ? i.classification == data.classification : true))
        .map((i) => i.year)
    );
    return [{ name: 'Busca un año', value: null }, ...Array.from(yearsMap).map((i) => ({ name: i, value: i }))].sort(
      (a, b) => a.value - b.value
    );
  });
  // Se calcula el listado de modelos por producto y año
  public readonly productListFiltered = computed(() => {
    const data = this._filteredData();

    return this.allModels()
      .filter(
        (i) =>
          (data.year ? i.year == data.year : true) &&
          (data.classification ? i.classification == data.classification : true) &&
          i.modelType === data.productType &&
          i.enabled
      )
      .map((i) => ({
        id: i.id,
        label: i.name,
        value: i.id,
        modelCode: i.modelCode,
        modelType: i.modelType,
        classification: i.classification,
        year: i.year,
        enabled: i.enabled,
      }));
  });
  // Estos son los campos que se van a observar para detectar cambios de edicion cuando haya un seguro cotizado
  // Ademas de agregar los campos al array, se deben agregar al html la directiva y el atributo: data-select-id="'XXX'" Y dataSelectId
  private readonly controlsToWatch: string[] = controlWatchingArray;
  private readonly watchInsurance = controlsInsuranceUpdateArray;

  constructor(route: ActivatedRoute, menu: MenusServices, notification: NotificationService) {
    super(route, menu, notification);
    this.createFormDeal();
  }

  ngOnInit() {
    this.formGroup.valueChanges
      .pipe(startWith(this.formGroup.value), pairwise(), takeUntilDestroyed(this._destroyRef))
      .subscribe(([previous, current]) => {
        for (const key in current) {
          if (current[key] !== previous[key]) {
            if (this.watchInsurance.includes(key)) {
              this.updateInsuranceData();
            }
          }
        }
      });

    // START DIRO - SECCION
    this.diroService.connect();

    this.diroService.statusChange$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((status) => {
      console.log('Status DIRO:', status);
    });
    this.diroService.responseReceived$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(({ setQuote, data }) => {
      console.log('Response DIRO:', data);
      this.calculateDiroComponent.setDataFromDiro(this.diroService.diroResponseDto);
      this.ctr['roRac'].setValue(data.data!.rorac);
      if (setQuote) {
        this._quote = data.data;
      }
    });
    this.diroService.modalRequired$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((modal) => {
      this.openModalConfirmation();
    });
    this.diroService.error$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((error) => {
      this.showNotificationError(error);
    });
    // END DIRO - SECCION

    this.updateInsuranceData();

    // Se activa en la primera carga de la pagina en el @input _deal
    this._catalogTrigger.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((quote) => {
      const isEditQuote = !!quote; // null o undefined → false=Es nueva,True=Es edicion
      const isInsuranceChecked = Boolean(quote?.includesInsurance);
      const isQuoteWithCatalog = isEditQuote && isInsuranceChecked;
      const productType = quote?.productType ? quote?.productType : this.ctr['productType'].value;
      this.showImages.productType = productType;
      this.lockUpdate = true;
      if (isEditQuote) {
        this.formGroup.patchValue(quote);
        this.previousFormGroup = this.formGroup.getRawValue();
        this.updateForm(quote);
      }
      if (!isInsuranceChecked) {
        this.ctr['modelId'].setValidators(null);
        this.ctr['year'].setValidators(null);
        // Brand y subbrand solo si no esta checked el brokerCatalogo
        const currentImg = this.ctr['subBrandUrl'].value;
        this.setBrandAndSubBrand()?.add(() => {
          if (!currentImg) {
            const subBrandId = this.ctr['subBrandId'].value;
            if (!subBrandId) {
              this.setDefaultImage(productType);
            } else {
              this.getFile(subBrandId, productType);
            }
          }
        });
      } else {
        this.fetchDataIfChangeModelOrClassification(() => {
          if (isQuoteWithCatalog) {
            const modelId = quote?.modelId;
            if (!this._selectedModel) {
              this.ctr['modelId'].setValue(modelId);
              const modelSelected: any = this.allModels().find((i) => i.id == modelId);
              this._selectedModel = modelSelected;
              this.quoteService.residualValue = modelSelected?.residualModelValues ?? [];
            }
            this.updateInsuranceData();
            if (quote?.insurance) {
              this.insuranceService.setInsuranceForm.next(quote?.insurance);
            }
          }
        });
      }
      this.isStarting.set(false);
      this.lockUpdate = false;
    });

    // interval(15000).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(()=>{
    //   console.log("Validando FORM");
    //   console.log("Validacion del valor isValid: ", {
    //     getRawValue: this.formGroup.getRawValue(),
    //     financialAmount: this.formGroup.valid,
    //     result: this.formGroup.get('result')?.value,
    //   });
    //   console.log('FORM status=', this.formGroup.status, 'valid=', this.formGroup.valid);
    //   console.log('FORM group errors=', this.formGroup.errors);
    //   for (const key in this.formGroup.controls) {
    //     const control = this.formGroup.controls[key];
    //     if (control.invalid) {
    //       console.log(`El campo ${key} tiene el siguiente error:`, control.errors);
    //     }
    //   }
    //   console.log("FIN validacion")
    // });
  }

  ngAfterViewInit() {
    this.controlElements.forEach((element: any) => {
      const fieldName = element.elementRef?.nativeElement?.getAttribute?.('data-select-id');
      if (!fieldName || !this.controlsToWatch.includes(fieldName)) return;

      const native: HTMLElement = element.elementRef?.nativeElement;

      // Handler para verificar seguro al hacer focus
      const pointerdownHandler = (event: PointerEvent) => {
        if (!this.isStarting()) {
          this.verifyInsurance(native, event, fieldName);
        }
      };

      // Agregar todos los event listeners necesarios
      native.addEventListener('pointerdown', pointerdownHandler, true);
      this._focusListeners.push(() => {
        native.removeEventListener('pointerdown', pointerdownHandler, true);
      });
    });
  }

  ngOnDestroy() {
    this._focusListeners.forEach((unregisterFn: any) => unregisterFn());
    this._focusListeners = [];
  }

  updateForm(quote: any) {
    this.ctr['includesInsurance'].setValue(!quote?.includesInsurance ? false : true);
    this.ctr['year'].setValue(quote?.openQuoteModel?.year);
    this._filteredData.set({
      year: quote?.openQuoteModel?.year ? quote?.openQuoteModel?.year : '',
      productType: quote?.productType,
      classification: quote?.classification === 'NEW' ? true : quote?.classification != 'USED',
    });
    const savedImg = quote?.openQuoteSubBrand?.subBrandUrl;
    if (savedImg) {
      this.imageSB = savedImg;
      this.showImg = true;
      this.showImages.hasSubbrandImg = true;
      this.ctr['subBrandUrl'].setValue(savedImg);
    } else {
      this.showImages.hasSubbrandImg = false;
      this.setDefaultImage(quote?.productType);
    }

    const hasResult = quote?.result != null;
    if (hasResult) {
      this.highlightFields = this.financialCalculator.getApiResultHighlight(this.formGroup);
    } else {
      this.highlightFields = this.financialCalculator.emptyHighlightFields;
    }
    // Para capLoan y realEstate, asegurar que quantity sea 1 después de cargar los datos
    if (this.isCapLoanOrRealEstate()) {
      this.ctr['quantity'].setValue(1);

      // Si unitPriceWithoutTax es 0 pero unitPriceWithTax tiene valor, calcular automáticamente el precio sin IVA
      const unitPriceWithoutTax = this.ctr['unitPriceWithoutTax'].value;
      const unitPriceWithTax = this.ctr['unitPriceWithTax'].value;
      const tax = this.ctr['tax'].value;

      if ((!unitPriceWithoutTax || unitPriceWithoutTax === 0) && unitPriceWithTax && tax) {
        const taxDecimal = parseInt(tax) / 100;
        const calculatedPriceWithoutTax = this.financialCalculator.calculateWithoutIVA(unitPriceWithTax, taxDecimal);
        this.ctr['unitPriceWithoutTax'].setValue(calculatedPriceWithoutTax);
      }
    }
    // Configurar validadores para el producto financiero cuando se carga una cotización existente
    this.setInitValidatorsForFinancialProduct(false);
    this.onlyCalculateDifferenceFinancialAmount();
    this.onlyCalculateDifferenceOpeningFee();
    this.onlyCalculateDifferenceSubsidy();

    this.calculatePurchaseOption('purchaseTotalWithTaxPrecalc');
    this.calculateSubsidyOptions('subsidyTotalWithTaxPrecalc');
    this.calculateResidualValueOption('residualValueTotalPrecalc');
    this.performCommissionCalculationForOpening();
    const warrantyDeposit = this.ctr['warrantyDeposit'].value;
    if (warrantyDeposit == null) {
      this.ctr['warrantyDepositPercentage'].setValue(null);
      this.ctr['warrantyDepositTotal'].setValue(null);
      this.ctr['warrantyDepositMonth'].setValue(null);
    }
    this.updateLastChangesForFormGroup();
  }

  createFormDeal() {
    this.formGroup = this.fb.group(
      {
        id: [null, []],
        creationDate: [null, []],
        dealName: ['', [Validators.required]],
        isSelected: [false, []],
        dealIsLoyalty: [false, []],
        roRac: ['', []],
        currencyType: ['MXN', [Validators.required]],
        quoteUsd: ['', []],
        tax: [16, Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)],
        taxFree: [false, [Validators.required]],
        customerRate: ['', [Validators.required, Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        financialProductType: [FinancialProductEnum.directCredit, [Validators.required]],
        term: ['', [Validators.required, Validators.max(Constants.MAX_TERMS_FOR_NORMAL_PRODUCT), Validators.min(Constants.MIN_TERMS_FOR_NORMAL_PRODUCT)]],
        productType: [null, [Validators.required]],
        quantity: ['', [Validators.required, Validators.pattern(/^[1-9]\d*$/), Validators.max(Constants.MAX_QUANTITY_FOR_NORMAL_PRODUCT)]],
        brandId: ['', []],
        subBrandId: ['', []],
        feature: ['', []],
        opexSegment: ['', []],
        classification: ['NEW', []],
        useTime: ['', [Validators.required, Validators.min(Constants.MIN_USE_TIME_FOR_PRODUCTS)]],
        unitPriceWithoutTax: ['', [Validators.required, Validators.min(Constants.MIN_UNIT_PRICE_FOR_PRODUCTS)]],
        unitPriceWithTax: ['', [Validators.required, Validators.min(Constants.MIN_UNIT_PRICE_FOR_PRODUCTS)]],
        totalPriceWithoutTax: ['', [Validators.min(Constants.MIN_UNIT_PRICE_FOR_PRODUCTS)]],
        totalPriceWithTax: ['', [Validators.min(Constants.MIN_UNIT_PRICE_FOR_PRODUCTS)]],
        downPaymentTaxType: [TaxType.invoiceWithTax, [Validators.required]],
        downPaymentTax: [, [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        downPayment: [, []],
        downPaymentWithTax: [, []],
        downPaymentWithoutTax: [, []],
        financialAmount: ['', []],
        openingFeeType: [null, []],
        openingFee: [null, []],
        openingFeeWithTax: ['', []],
        openingFeeWithoutTax: ['', []],
        openingFeePercentage: ['', [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        warrantyDeposit: [null, []],
        warrantyDepositTotal: ['0', []],
        warrantyDepositMonth: ['0', []],
        warrantyDepositPercentage: ['', [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        subsidyType: [null, []],
        subsidyPercentage: ['', [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        subsidyTotal: ['', []],
        subsidyTotalWithTax: ['', []],
        subsidyTotalWithoutTax: ['', []],
        balloonType: ['', []],
        balloonPercentage: ['', [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        balloon: ['', []],
        balloonWithTax: ['', []],
        balloonWithoutTax: ['', []],
        purchaseOptionType: [null, []],
        purchasePercentage: [, [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        purchaseTotal: [, []],
        purchaseTotalWithTax: [, []],
        purchaseTotalWithoutTax: [, []],
        residualValueType: [null, []],
        residualValuePercentage: [, [Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/)]],
        residualValueTotal: [, []],
        residualValueTotalWithoutTax: [, []],
        residualValueTotalWithTax: [, []],
        paymentType: ['EXPIRATION', [Validators.required]],
        paymentFormationType: ['LEVELED', [Validators.required]],
        confirmationExpensesAmount: [, []],
        lifeInsuranceFactor: [, [Validators.max(2), Validators.min(0)]],
        additionalCollateralType: [null, []],
        additionalCollateralPercentage: [, []],
        additionalCollateralTotal: [, []],
        profitDeal: [, []],
        specialPayments: [[], []],
        grace: [[], []],
        irregularPayments: [[], []],
        payments: [[], []],
        monthlyPayment: [null, []],
        insuranceDeals: [[], []],
        insurance: [null, []],
        result: [null, []],
        year: ['', [Validators.required]],
        modelId: ['', [Validators.required]],
        includesInsurance: [true, []],
        openQuoteBrand: [null, []],
        openQuoteSubBrand: [null, []],
        openQuoteModel: [null, []],
        subBrandUrl: ['', []],
      },
      {
        validators: [
          OpenQuoteValidations.validateWarrantyDepositTypeAllowedForProduct('warrantyDeposit', 'financialProductType'),
          OpenQuoteValidations.validateWarrantyDepositFieldsByType(
            'warrantyDeposit',
            'warrantyDepositPercentage',
            'warrantyDepositMonth',
            'warrantyDepositTotal'
          ),
          OpenQuoteValidations.validateWarrantyDepositMonthsLessThanTerm(
            'warrantyDeposit',
            'warrantyDepositMonth',
            'term'
          ),
        ],
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customer'] && this.formGroup) {
      this.syncLoyaltyControlWithFeriaCustomer();
    }
  }

  private syncLoyaltyControlWithFeriaCustomer(): void {
    if (!this.featureLoyaltyFeriaQuotation || !this.formGroup) {
      return;
    }
    const ctrl = this.formGroup.get('dealIsLoyalty');
    if (!ctrl) {
      return;
    }
    if (this.customerFeriaForLoyaltyUi()) {
      ctrl.setValue(false, { emitEvent: false });
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  /** Cliente feria desde el formulario de open quote (evita truthy por string `"false"`). */
  customerFeriaForLoyaltyUi(): boolean {
    return coerceOpenQuoteBoolean(this.customer?.feriaCustomer);
  }

  calculateDiro(e: any) {
    if (!this.diroService.activeDiro) return;
    let model = this.financialCalculator.getBodyforCalculateQuote(this.formGroup, this.nameCompany());
    let stringBaseInformation: string | null = window.sessionStorage.getItem(this.diroService.BASE_INFORMATION);
    let baseInformation = JSON.parse(stringBaseInformation!);
    let others = this.diroService.getOtherDiroDto(this.formGroup);
    let usr = {
      upp: window.sessionStorage.getItem(this.diroService.UPP),
      upm: window.sessionStorage.getItem(this.diroService.UPM),
    };
    if (usr.upm && usr.upp) {
      this.diroService.startDiroOperations(model, baseInformation, usr, others);
    } else {
      this.diroService.modelDiro = model;
      this.diroService.baseInfoDiro = baseInformation;
      this.diroService.othersDiro = others;
      this.modalService.component = this.modalDiroLogin()!;
      this.modalService.openModal();
    }
  }

  closedDiroModal(result?: any) {
    if (result) {
      this.diroService.startDiroOperations(
        this.diroService.modelDiro,
        this.diroService.baseInfoDiro,
        result,
        this.diroService.othersDiro
      );
    }
    this.modalService.closeModal();
  }

  getSpecialPaymentsOutOfRange(validatedTerm: number) {
    const hasGraceGreaterThan = this.validationsService.hasPeriodGreaterThanAndNotEqualPayments(this.ctr['grace'].value, validatedTerm);
    const hasSpecialGreaterThan = this.validationsService.hasPeriodGreaterThan(this.ctr['specialPayments'].value, validatedTerm);
    const hasIrregularGreaterThan = this.validationsService.hasPeriodGreaterThan(this.ctr['irregularPayments'].value, validatedTerm);
    return {
      hasGraceGreaterThan,
      hasSpecialGreaterThan,
      hasIrregularGreaterThan,
      hasPaymentSpecialInOutRange: hasGraceGreaterThan || hasSpecialGreaterThan || hasIrregularGreaterThan,
    };
  }

  validatePaymentsOutOfRange() {
    const validatedTerm = this.ctr['term'].value;
    const { hasPaymentSpecialInOutRange } = this.getSpecialPaymentsOutOfRange(validatedTerm);
    if (!hasPaymentSpecialInOutRange) return true;

    this.openModalDeletePaymentConfirmation();
    return false;
  }

  calculateQuote(calcId: string, version: string = 'v1') {
    if (this.isActivePaymentFixed) {
      if (!this.validatePaymentsOutOfRange()) {
        return;
      }
    }
    this.loading.begin();
    const insurance = this.ctr['insurance'].value;
    const insuranceType = insurance?.insuranceType;
    const hasMarshInsurancePayments =
      this.isActiveBrokerCatalog &&
      (insuranceType == InsuranceTypeEnum.FINANCED_ANNUAL || insuranceType == InsuranceTypeEnum.FINANCED_MULTIYEAR);

    let model = this.financialCalculator.getBodyforCalculateQuote(this.formGroup, this.nameCompany());
    const data = this.cleanModelCalculatedData(model);
    this.applyLifeInsuranceCalculatePayload(data, insurance);
    if (this.reCalculateInsuranceDealPayments) {
      this.handleCalculateQuoteAndManualInsurance(data, version);
    } else if (hasMarshInsurancePayments) {
      this.handleCalculateQuoteAndMarshInsurance(data, insurance, version);
    } else {
      this.handleCalculateQuote(data, version);
    }
    this.loading.end();
  }

  /** Caso 1: omitir amount. Caso 2: amount = prima unidad financiada. */
  private applyLifeInsuranceCalculatePayload(data: any, insurance: any) {
    const personType = this.customer?.personType;
    const financialProduct = this.ctr['financialProductType'].value;
    const eligible = isLifeInsuranceEligible(personType, financialProduct);
    data.personType = personType;
    if (!eligible) {
      data.lifeInsuranceFactor = 0;
      delete data.insuranceAmount;
      return;
    }
    const premium = resolveLifeInsurancePremium(
      insurance,
      this.ctr['insuranceDeals'].value,
    );
    if (premium != null) {
      data.insuranceAmount = premium;
    } else {
      delete data.insuranceAmount;
    }
  }

  currencySelected() {
    const currency = this.ctr['currencyType'].value;
    this.currency = currency;
    return this.financialCalculator.currencies.find((i) => i.value == currency)?.type;
  }

  onBrandChange(event: Event): void {
    const selectedId = (event.target as HTMLSelectElement).value;
    const brandId = Number(selectedId);
    if (!isNaN(brandId)) {
      this.ctr['brandId'].setValue(brandId);
      this.formGroup.get('brandId')?.setValue(brandId);
      if (this.isAccesoriesProduct) {
        this.filterAccessorySubBrandsByBrand(brandId);
      } else {
        this.selectProductAndSetBrandsAndSubBrands();
      }
    } else {
      this.subBrands = [];
      this.formGroup.get('brandId')?.reset();
    }
    if (!isNaN(brandId) && this.brands) {
      const brand = this.brands.find((br) => br.id == brandId);
      const feature = this.ctr['feature'].value ?? this.formGroup.get('feature')?.value ?? "";
      if (this.validationsService.hasFeatureWithDefaultText(feature)) this.ctr['feature'].setValue(brand.name);
    }
  }

  onSubBrandChange(event: Event): void {
    const selectedId = (event.target as HTMLSelectElement).value;
    const subBrandId = Number(selectedId);
    const productType = this.formGroup.get('productType')?.value;

    if (!isNaN(subBrandId) && productType) {
      this.ctr['subBrandId'].setValue(subBrandId);
      this.formGroup.get('subBrandId')?.setValue(subBrandId);
      if (!this.isAccesoriesProduct) {
        this.selectProductAndSetBrandsAndSubBrands();
      }
      this.getFile(subBrandId, productType);
    } else {
      this.formGroup.get('subBrandId')?.reset();
    }
  }

  getFile(id: number, productType: any): void {
    this.loading.track(this.subBrandService.getAttachmentByElementId(id, FileType.SUB_BRAND))
      .pipe(
        take(1)
      )
      .subscribe({
        next: (response: any) => {
          if (response.data.length > 0) {
            const row = response.data[0];
            const displayUrl = row.publicUrl || row.urlSource;
            this.imageSB = displayUrl;
            this.showImg = true;
            this.showImages.hasSubbrandImg = true;
            this.ctr['subBrandUrl'].setValue(displayUrl);
          } else {
            this.showImages.hasSubbrandImg = false;
            this.setDefaultImage(productType);
          }
        },
        error: (error) => {
          this.showImg = false;
          if (error.status === 404) {
            this.showImages.hasSubbrandImg = false;
            this.setDefaultImage(productType);
            return;
          }
          console.error('Error al obtener la imagen:', error);
        },
      });
  }

  setDefaultImage(productType: string): void {
    if (!productType) {
      this.imageSB = null;
      this.showImg = false;
      this.showImages.hasSubbrandImg = false;
      return;
    }
    if (!this.showImages.hasSubbrandImg) {
      switch (productType?.toUpperCase()) {
        case productTypeEnum.accesorios:
          this.imageSB = vehicles.accesorios;
          this.showImages.hasSubbrandImg = true;
          break;
        case productTypeEnum.vanes:
          this.imageSB = vehicles.sprinter;
          this.showImages.hasSubbrandImg = true;
          break;
        case productTypeEnum.autobuses:
          this.imageSB = vehicles.autobus;
          this.showImages.hasSubbrandImg = true;
          break;
        case productTypeEnum.camiones:
          this.imageSB = vehicles.camion;
          this.showImages.hasSubbrandImg = true;
          break;
        case productTypeEnum.tractos:
          this.imageSB = vehicles.cascadia;
          this.showImages.hasSubbrandImg = true;
          break;
        case productTypeEnum.nonDamiler:
          this.imageSB = vehicles.nondaimler;
          this.showImages.hasSubbrandImg = true;
          break;
        default:
          this.showImages.hasSubbrandImg = false;
          this.imageSB = null;
      }
      this.ctr['subBrandUrl'].setValue('');
    }

    this.showImg = !!this.imageSB;
  }

  selectProduct(): void {
    this.validateAndExecute(() => {
      const product = this.ctr['productType'].value;
      const subBrandId = this.ctr['subBrandId'].value;
      this.showImages.hasSubbrandImg = false;
      if (this.isActiveBrokerCatalog) {
        this.ctr['year'].setValue(null);
        this.ctr['year'].updateValueAndValidity({ onlySelf: true });
        if (this.isAccesoriesProduct && this.isActiveAccesoriesMarsh) {
          this.ctr['classification'].setValue(null);
          this.ctr['classification'].updateValueAndValidity({ onlySelf: true });
        }
        this.updateInsuranceData();
        // Forzar la limpieza de datos y la recarga
        this.fetchDataIfChangeModelOrClassification();
        this.cleanModelData();
        this._filteredData.update((curr) => ({ ...curr, productType: product, year: '', classification: null }));
        this.insuranceService.resetForm.next();
        this.defineSubBrandImage(product, 0);
      } else {
        this.ctr['modelId'].setValidators(null);
        this.ctr['year'].setValidators(null);
        this.ctr['modelId'].setValue(null);
        this._selectedModel = null;
        this.ctr['year'].setValue(null);
        this.setBrandAndSubBrand()?.add(() => {
          this.defineSubBrandImage(product, subBrandId);
        });
      }
    });
  }

  setBrandAndSubBrand() {
    if (!this.formGroup || this.isActiveBrokerCatalog) return;
    const product = this.ctr['productType'].value;
    const subBrandId = this.ctr['subBrandId'].value;
    let brandId = this.formGroup.get('brandId')?.value || null;
    if (product === productTypeEnum.accesorios) {
      return this.setAccessoryBrandsAndSubBrands();
    }
    if (product === productTypeEnum.autobuses) {
      if (this.validationsService.hasFeatureWithDefaultText(this.ctr['feature'].value)) this.ctr['feature'].setValue('Mercedes Benz');
      this.ctr['brandId'].setValue(1);
      brandId = 1;
    } else if (product === productTypeEnum.camiones || product === productTypeEnum.tractos) {
      if (this.validationsService.hasFeatureWithDefaultText(this.ctr['feature'].value)) this.ctr['feature'].setValue('Freightliner');
      this.ctr['brandId'].setValue(2);
      brandId = 2;
    }
    this.body = {
      modelType: product,
      brandId: brandId,
      subBrandId: subBrandId,
    };


    return this.loading.track(this.quoteService.getBrandsAndSubBrands(this.body))
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: any) => {
          const data = response?.data || {};
          this.models = data?.models || [];
          this.brands = (data?.brands || []).filter((brand: any) => brand.enabled);
          this.subBrands = (data?.subBrands || []).filter((subBrand: any) => subBrand.enabled);
        },
        error: (err) => console.error('Error al obtener marcas y submarcas', err),
      });
  }

  setAccessoryBrandsAndSubBrands() {
    return this.loading.track(this.brandService.getAllBrands({
      name: null,
      brandCode: null,
      enabled: true,
      pageSize: 1000,
      page: 1,
    }))
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (brandResponse: any) => {
          const accessoryBrands = (brandResponse?.data || []).filter((brand: any) =>
            brand?.enabled && this.isAccessoryBrand(brand)
          );
          const accessoryBrandId = accessoryBrands[0]?.id;
          this.brands = accessoryBrands;
          this.models = [];
          if (!accessoryBrandId) {
            this.accessorySubBrands = [];
            this.subBrands = [];
            this.selectFirstAccessoryBrandAndSubBrand();
            return;
          }
          this.setAccessorySubBrandsByBrand(accessoryBrandId);
        },
        error: (err) => console.error('Error al obtener marca de accesorios', err),
      });
  }

  setAccessorySubBrandsByBrand(brandId: number) {
    return this.loading.track(this.subBrandService.getAllSubBrands({
        name: null,
        subBrandCode: null,
        enabled: true,
        brandId,
        pageSize: 1000,
        page: 1,
      }))
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (subBrandResponse: any) => {
          this.accessorySubBrands = this.getUniqueById((subBrandResponse?.data || []).filter((subBrand: any) => subBrand?.enabled));
          this.subBrands = this.accessorySubBrands;
          this.selectFirstAccessoryBrandAndSubBrand();
        },
        error: (err) => console.error('Error al obtener submarcas de accesorios', err),
      });
  }

  isAccessoryBrand(brand: any): boolean {
    return brand?.name?.trim().toLowerCase().includes('accesorio');
  }

  getUniqueById(items: any[]): any[] {
    const uniqueItems = new Map<number, any>();
    items.forEach((item) => {
      if (!uniqueItems.has(item.id)) {
        uniqueItems.set(item.id, item);
      }
    });
    return Array.from(uniqueItems.values());
  }

  selectFirstAccessoryBrandAndSubBrand() {
    const selectedBrandId = this.ctr['brandId'].value;
    const selectedSubBrandId = this.ctr['subBrandId'].value;
    const brandId = this.brands.some((brand) => brand.id == selectedBrandId) ? selectedBrandId : this.brands[0]?.id;
    const subBrandId = this.subBrands.some((subBrand) => subBrand.id == selectedSubBrandId)
      ? selectedSubBrandId
      : this.subBrands[0]?.id;

    this.ctr['brandId'].setValue(brandId ?? null);
    this.formGroup.get('brandId')?.setValue(brandId ?? null);
    this.ctr['subBrandId'].setValue(subBrandId ?? null);
    this.formGroup.get('subBrandId')?.setValue(subBrandId ?? null);
  }

  filterAccessorySubBrandsByBrand(brandId: number) {
    const filteredSubBrands = this.accessorySubBrands.filter((subBrand: any) =>
      (subBrand?.brand?.id ?? subBrand?.brandId) == brandId
    );
    this.subBrands = filteredSubBrands.length ? filteredSubBrands : this.accessorySubBrands;
    const selectedSubBrandId = this.ctr['subBrandId'].value;
    if (!this.subBrands.some((subBrand) => subBrand.id == selectedSubBrandId)) {
      const subBrandId = this.subBrands[0]?.id ?? null;
      this.ctr['subBrandId'].setValue(subBrandId);
      this.formGroup.get('subBrandId')?.setValue(subBrandId);
    }
  }

  selectProductAndSetBrandsAndSubBrands() {
    if (!this.formGroup) return;
    const product = this.ctr['productType'].value;

    this.body = {
      modelType: product,
      brandId: this.formGroup.get('brandId')?.value || null,
      subBrandId: this.formGroup.get('subBrandId')?.value || null,
    };

    return this.loading.track(this.quoteService.getBrandsAndSubBrands(this.body))
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: any) => {
          const data = response?.data || {};
          this.models = data.models || [];
          this.brands = (data.brands || []).filter((brand: any) => brand.enabled);
          this.subBrands = (data.subBrands || []).filter((subBrand: any) => subBrand.enabled);
        },
        error: (err) => console.error('Error al obtener marcas y submarcas', err),
      });
  }

  selectFinacialProduct() {
    this.validateAndExecute(() => {
      this.insuranceService.resetForm.next();
      this.fixTermWhenChangeFinancialProduct();
      this.setInitValidatorsForFinancialProduct(true);
      this.calculateFinacialAmount();
      this.calculateUnitPriceWithoutTax('unitPriceWithoutTax');
      this.resetMarshAndManualInsurancePayments();
    });
  }

  fixTermWhenChangeFinancialProduct() {
    const { term, minTerm, maxTerm } = this.validationsService.getDataForMinMaxTermBusinessRules(this.ctr['term'].value, this.ctr['financialProductType'].value);
    if (term) {
      if (term < minTerm) {
        this.ctr['term'].setValue(minTerm);
      } else if (term > maxTerm) {
        this.ctr['term'].setValue(maxTerm);
      }
    }
  }

  taxFreeBusinessRules() {
    const { taxFree, result } = this.validationsService.taxFreeBusinessRules(this.ctr['taxFree'].value, this.ctr['financialProductType'].value, this.nameCompany());
    this.ctr['taxFree'].setValue(taxFree);
    if (result !== undefined) {
      this.ctr['result'].setValue(result);
    }
  }

  lifeInsuranceFactorBusinessRules() {
    const { lifeInsuranceFactor, result } = this.validationsService.lifeInsuranceFactorBusinessRules(this.ctr['lifeInsuranceFactor'].value);
    if (lifeInsuranceFactor !== undefined) {
      this.ctr['lifeInsuranceFactor'].setValue(lifeInsuranceFactor);
      if (result !== undefined) {
        this.ctr['result'].setValue(result);
      }
    }
  }

  minMaxTermBusinessRules() {
    const { term, minTerm, maxTerm } = this.validationsService.getDataForMinMaxTermBusinessRules(this.ctr['term'].value, this.ctr['financialProductType'].value);
    if (!term && term !== 0) return;
    this.ctr['term'].setValidators([
      Validators.required,
      Validators.max(maxTerm),
      Validators.min(minTerm),
    ]);
    this.ctr['term'].updateValueAndValidity();
    const warrantyDepositMonth = this.ctr['warrantyDepositMonth'].value;
    const hasDepositMonth = this.ctr['warrantyDeposit'].value === TaxType.numberOfRents;
    if (term < minTerm) {
      this.ctr['term'].setValue(minTerm);
      this.showNotificationError('El plazo mínimo para este producto es de ' + minTerm + ' meses.');
    } else if (term > maxTerm) {
      this.ctr['term'].setValue(maxTerm);
      this.showNotificationError('El plazo máximo para este producto es de ' + maxTerm + ' meses.');
    }
    const newTerm = this.ctr['term'].value;
    if (hasDepositMonth && warrantyDepositMonth >= newTerm) {
      this.ctr['warrantyDepositMonth'].setValue(newTerm - 1);
    }
  }

  newBalloonBusinessRules() {
    if (!this.isActiveBallon) return; // Si el balloon no esta habilitado o ya hay un valor guardo, no hace nada
    const balloonType = this.ctr['balloonType'].value;
    const fnProductType = this.ctr['financialProductType'].value;
    const hasValueInBalloonType = balloonType && balloonType !== null && balloonType !== undefined;
    const isDirectCredit = fnProductType === FinancialProductEnum.directCredit;
    const isFinancialLeasing = fnProductType === FinancialProductEnum.financialLeasing;
    if (isDirectCredit) {
      this.ctr['financialProductType'].setValue(FinancialProductEnum.directCreditGlobe);
    }
    const isDirectCreditBalloon = fnProductType === FinancialProductEnum.directCreditGlobe;
    if (!hasValueInBalloonType && (isDirectCredit || isFinancialLeasing || isDirectCreditBalloon)) {
      this.ctr['balloonType'].setValue(null);
      this.ctr['balloonPercentage'].setValue(null);
      this.ctr['balloon'].setValue(null);
      this.formGroup.updateValueAndValidity();
    }
  }

  setInitValidatorsForFinancialProduct(isInitialized = false) {
    let financialProductSelected = this.ctr['financialProductType'].value;
    let fieldsRequired: string[] = [];
    let fieldsRemoveValidators: string[] = [];
    let fieldsSetValueAndErrors: string[] = [];
    this.taxFreeBusinessRules();
    this.lifeInsuranceFactorBusinessRules();
    this.setUseTimeValidators();
    this.minMaxTermBusinessRules();
    this.newBalloonBusinessRules();
    if (financialProductSelected == FinancialProductEnum.financialLeasing) {
      //financiero
      fieldsRequired = ['purchaseOptionType', 'downPaymentTaxType', 'productType', 'quantity'];
      fieldsRemoveValidators = [
        'residualValueType',
        'residualValuePercentage',
        'residualValueTotal',
        'residualValueTotalWithoutTax',
        "warrantyDeposit"
      ];
      fieldsSetValueAndErrors = [
        'additionalCollateralType',
        'additionalCollateralPercentage',
        'additionalCollateralTotal',
        'residualValueType',
        'residualValuePercentage',
        'residualValueTotal',
        'residualValueTotalWithoutTax'
      ];

      if (!this.ctr['downPaymentTaxType']?.value) {
        this.ctr['downPaymentTaxType'].setValue(TaxType.invoiceWithTax);
      }
      if (!this.ctr['purchaseOptionType']?.value) {
        this.ctr['purchaseOptionType'].setValue(TaxType.invoiceWithoutTax);
      }
      if (this.isActiveBrokerCatalog) {
        fieldsRequired.push('year');
        fieldsRequired.push('modelId');
      }

      const purchaseOptionType = this.ctr['purchaseOptionType'].value;
      const downPaymentTaxType = this.ctr['downPaymentTaxType'].value;
      if (downPaymentTaxType != TaxType.fixedAmount) {
        fieldsRequired.push('downPaymentTax');
      }
      if (purchaseOptionType != TaxType.fixedAmount) {
        fieldsRequired.push('purchasePercentage');
      } else if (purchaseOptionType == TaxType.fixedAmount) {
        fieldsRequired.push('purchaseTotal');
      }

      this.ctr['residualValueType'].setValue(null);
      this.ctr['residualValuePercentage'].setValue(null);

      if (isInitialized) {
        this.ctr['residualValueTotal'].setValue(null);
        this.ctr['residualValueTotalWithoutTax'].setValue(null);

        this.ctr['purchasePercentage'].setValue(null);
        this.ctr['purchaseTotal'].setValue(null);
        this.ctr['purchaseTotalWithTax'].setValue(null);
      }
    } else if (financialProductSelected == FinancialProductEnum.pureLease) {
      //puro
      fieldsRequired = ['residualValueType', 'warrantyDeposit', 'productType', 'quantity'];
      fieldsRemoveValidators = [
        'downPaymentTaxType',
        'downPaymentTax',
        'downPayment',
        'purchaseOptionType',
        'purchasePercentage',
        'purchaseTotal',
        'balloonType',
        'balloonPercentage',
        "balloon"
      ];
      fieldsSetValueAndErrors = [
        'additionalCollateralType',
        'additionalCollateralPercentage',
        'additionalCollateralTotal',
        'downPaymentTaxType',
        'downPaymentTax',
        'downPayment',

        'purchaseOptionType',
        'purchasePercentage',
        'purchaseTotal',

        'balloonType',
        'balloonPercentage',
        "balloon"
      ];
      this.ctr['downPayment'].setValue(0);
      this.ctr['downPaymentTax'].setValue(null);
      this.ctr['downPaymentTaxType'].setValue(null);

      this.ctr['purchasePercentage'].setValue(null);
      this.ctr['purchaseOptionType'].setValue(null);

      if (!this.ctr['residualValueType']?.value) {
        this.ctr['residualValueType'].setValue(TaxType.invoiceWithoutTax);
      }
      if (this.isActiveBrokerCatalog) {
        fieldsRequired.push('year');
        fieldsRequired.push('modelId');
      }

      if (isInitialized) {
        this.ctr['purchaseTotal'].setValue(null);
        this.ctr['purchaseTotalWithTax'].setValue(null);

        this.ctr['residualValuePercentage'].setValue(null);
        this.ctr['residualValueTotal'].setValue(null);
        this.ctr['residualValueTotalWithoutTax'].setValue(null);
      }

      if (!this.ctr['warrantyDeposit'].value) {
        this.ctr['warrantyDeposit'].setValue(TaxType.numberOfRents);
        this.ctr['warrantyDepositMonth'].setValue(0);
        this.ctr['warrantyDepositTotal'].setValue(0);

        this.ctr['warrantyDepositPercentage'].disable();
        this.ctr['warrantyDepositTotal'].disable();

        this.ctr['warrantyDepositPercentage'].setValue(null);
        this.ctr['warrantyDepositMonth'].enable();
        fieldsRequired.push('warrantyDepositMonth');
      }
      if (this.ctr['residualValueType'].value != TaxType.fixedAmount) {
        fieldsRequired.push('residualValuePercentage');
      }
    } else if (
      financialProductSelected == FinancialProductEnum.directCredit ||
      financialProductSelected == FinancialProductEnum.directCreditGlobe
    ) {
      //directo
      fieldsRequired = ['downPaymentTaxType', 'productType', 'quantity'];
      fieldsRemoveValidators = [
        'purchaseOptionType',
        'purchasePercentage',
        "purchaseTotal",
        'residualValueType',
        'residualValuePercentage',
        "residualValueTotal",
        "warrantyDeposit"
      ];
      fieldsSetValueAndErrors = [
        'purchaseOptionType',
        'purchasePercentage',
        'purchaseTotal',
        'purchaseTotalWithTax',

        'residualValueType',
        'residualValuePercentage',
        'residualValueTotal',
        'residualValueTotalWithoutTax',
      ];

      if (!this.ctr['downPaymentTaxType']?.value) {
        this.ctr['downPaymentTaxType'].setValue(TaxType.invoiceWithTax);
      }
      const downPaymentTaxType = this.ctr['downPaymentTaxType'].value;
      if (downPaymentTaxType != TaxType.fixedAmount) {
        fieldsRequired.push('downPaymentTax');
      }
      if (this.isActiveBrokerCatalog) {
        fieldsRequired.push('year');
        fieldsRequired.push('modelId');
      }

      this.ctr['purchaseOptionType'].setValue(null);
      this.ctr['purchasePercentage'].setValue(null);
      this.ctr['residualValueType'].setValue(null);
      this.ctr['residualValuePercentage'].setValue(null);

      if (isInitialized) {
        this.ctr['purchaseTotal'].setValue(null);
        this.ctr['purchaseTotalWithTax'].setValue(null);

        this.ctr['residualValueTotal'].setValue(null);
        this.ctr['residualValueTotalWithoutTax'].setValue(null);
      }
    } else if (
      financialProductSelected == FinancialProductEnum.capLoan ||
      financialProductSelected == FinancialProductEnum.realEstate
    ) {
      // Para préstamos, setear cantidad a 1 automáticamente
      this.ctr['quantity'].setValue(1);
      fieldsRequired = [
        'downPaymentTaxType',
      ];
      fieldsRemoveValidators = [
        'productType',
        'quantity',
        'warrantyDeposit',
        'year',
        'modelId',
        'balloonType',
        'balloonPercentage',
        'balloon',
        'purchaseOptionType',
        'purchasePercentage',
        'purchaseTotal',
        'purchaseTotalWithTax',
        'residualValueType',
        'residualValuePercentage',
        'residualValueTotal',
        'residualValueTotalWithoutTax',
      ];
      fieldsSetValueAndErrors = ['lifeInsuranceFactor'];

      if (!this.ctr['downPaymentTaxType']?.value) {
        this.ctr['downPaymentTaxType'].setValue(TaxType.invoiceWithTax);
      }
      const downPaymentTaxType = this.ctr['downPaymentTaxType'].value;
      if (downPaymentTaxType != TaxType.fixedAmount) {
        fieldsRequired.push('downPaymentTax');
      }
      if (this.ctr["insurance"].value != null) {
        this.ctr["insurance"].setValue(null);
        this.insuranceService.resetForm.next();
      }
    }

    this.setValidators(fieldsRequired);
    this.removeValidators(fieldsRemoveValidators);
    this.setValueAndErrors(fieldsSetValueAndErrors);
    this.formGroup.updateValueAndValidity({ onlySelf: true });
  }

  isCapLoanOrRealEstate(): boolean {
    const value = this.ctr['financialProductType'].value;
    return value === FinancialProductEnum.capLoan || value === FinancialProductEnum.realEstate;
  }

  validCapLoanOrRealEstate(): boolean {
    const value = this.ctr['financialProductType'].value;

    const isProductValid = value === FinancialProductEnum.capLoan || value === FinancialProductEnum.realEstate;

    const unitPriceWithoutTax = this.ctr['unitPriceWithoutTax'].value;
    const paymentType = this.ctr['paymentType'].value;
    const term = this.ctr['term'].value;
    const dealName = this.ctr['dealName'].value;
    const customerRate = this.ctr['customerRate'].value;
    const openingFeePercentage = this.ctr['openingFeePercentage'].value;

    const requiredFieldsFilled =
      unitPriceWithoutTax !== null &&
      paymentType !== null &&
      term !== null &&
      dealName !== '' &&
      customerRate !== null &&
      openingFeePercentage !== null;

    return isProductValid && requiredFieldsFilled;
  }

  calculateUnitPriceWithoutTax(id: string) {
    this.validateAndExecute(() => {
      this.resetAllAPIDifferenceSignals();
      this.ctr['result'].setValue(null);
      const unitPrice = this.ctr['unitPriceWithoutTax'].value;

      if (!unitPrice) {
        this.resetPriceRelatedFields(unitPrice, id);
      } else {
        this.calculateAndSetPriceWithTax(unitPrice);
      }

      this.executePostCalculationUpdates();
    });
    this.hideAmountInput(id);
  }

  calculateUnitPriceWithTax(id: string) {
    this.validateAndExecute(() => {
      this.resetAllAPIDifferenceSignals();

      const unitPrice = this.ctr['unitPriceWithTax'].value;
      const tax = this.ctr['tax'].value;

      if (!this.isValidTaxValue(tax)) {
        return;
      }

      if (!unitPrice) {
        this.resetPriceRelatedFields(unitPrice, id);
      } else {
        this.calculateAndSetPriceWithoutTax(unitPrice, tax);
        this.executePostCalculationUpdates();
        if (this.ctr['warrantyDepositTotal'].value > this.ctr['unitPriceWithTax'].value) {
          this.ctr['warrantyDepositTotal'].setValue(this.ctr['unitPriceWithTax'].value);
        }
      }
    });
    this.hideAmountInput(id);
  }

  changeQuantity() {
    this.validateAndExecute(() => {
      const raw = this.ctr['quantity'].value;
      const n = Number(raw);
      const min = Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT;
      if (!Number.isFinite(n) || n < min) {
        this.ctr['quantity'].setValue(min);
      }
      this.calculateTotal();
    });
  }

  calculateTotal() {
    const unitsQuantity = this.ctr['quantity'].value;

    if (!this.ctr['unitPriceWithTax'].value) return;
    const priceWithTax = this.ctr['unitPriceWithTax'].value;
    const priceWithoutTax = this.ctr['unitPriceWithoutTax'].value;

    // Para Capital Loan y Real Estate, el total es igual al precio unitario (no hay cantidad)
    if (this.isCapLoanOrRealEstate()) {
      this.ctr['totalPriceWithoutTax'].setValue(priceWithoutTax);
      this.ctr['totalPriceWithTax'].setValue(priceWithTax);
    } else {
      // Para otros productos, multiplicar por la cantidad
      if (!unitsQuantity) return;
      this.ctr['totalPriceWithoutTax'].setValue(priceWithoutTax * unitsQuantity);
      this.ctr['totalPriceWithTax'].setValue(priceWithTax * unitsQuantity);
    }
  }

  calculateDownPayment(id?: string) {
    this.validateAndExecute(() => {
      const type = this.ctr['downPaymentTaxType'].value;
      if (!type) {
        this.resetDownPaymentFields();
        return;
      }

      const validationResult = this.validateDownPaymentConfiguration(type);
      if (!validationResult.isValid) return;
      const calculationResult = this.setDownPaymentCalculation(type);
      this.executeDownPaymentPostProcessing(calculationResult.hadFormChanges, validationResult);
      this.formGroup.updateValueAndValidity({ onlySelf: true });
    });
    if (id) {
      this.hideAmountInput(id);
    }
  }

  setDownPaymentCalculation(type: any) {
    const calculationResult = this.performDownPaymentCalculation(type);
    this.applyDownPaymentResult(calculationResult);
    return calculationResult;
  }

  calculateFinacialAmount() {
    const financialProduct = this.ctr['financialProductType'].value;
    this.setFinancialAmountCalculation();
    // Ejecutar cálculos específicos por tipo de producto
    this.executeProductSpecificCalculations(financialProduct);
    // Ejecutar cálculos finales
    this.executeFinalCalculations();
  }

  setFinancialAmountCalculation() {
    const calculatedAmount = this.calculatePureFinancialAmount();
    const result = this.determineFinancialAmountValue(calculatedAmount);
    this.ctr['financialAmount'].setValue(result.finalValue);
    return result;
  }

  calculateFinacialAmountUpdate() {
    this.validateAndExecute(() => {
      this.calculateFinacialAmount();
    });
  }

  calculateCommissionForOpening() {
    this.validateAndExecute(() => {
      this.performCommissionCalculationForOpening(true);
      this.resetMarshAndManualInsurancePayments();
    });
  }

  changeSubsidy(id?: string, idHidden?: string) {
    const type = this.ctr['subsidyType']?.value;
    this.validateAndExecute(() => {
      let fieldsRequired: string[] = [];
      let fieldsRemoveValidators: string[] = [];

      if (!type || type == 'null') {
        fieldsRemoveValidators = ['subsidyPercentage', 'subsidyType', 'subsidyTotal'];
        this.removeValidators(fieldsRemoveValidators);
        this.setValueAndErrors(fieldsRemoveValidators);
        this.ctr['subsidyTotal'].setValue(null);
        this.ctr['subsidyPercentage'].setValue(null);
        this.ctr['subsidyType'].setValue(null);
        return;
      }

      if (type == TaxType.invoiceWithTax || type == TaxType.invoiceWithoutTax || type == TaxType.amountToFinance) {
        fieldsRequired = ['subsidyPercentage'];
        fieldsRemoveValidators = ['subsidyTotal'];
        this.ctr['subsidyTotal'].disable();
        this.ctr['subsidyPercentage'].enable();
        if (!this.ctr['subsidyPercentage'].value)
          this.ctr['subsidyPercentage'].setErrors({
            required: true,
          });
      } else if (type == TaxType.fixedAmount) {
        fieldsRequired = ['subsidyTotal'];
        fieldsRemoveValidators = ['subsidyPercentage'];
        this.ctr['subsidyPercentage'].disable();
        this.ctr['subsidyPercentage'].setValue(null);
        this.ctr['subsidyTotal'].enable();
      } else {
        this.ctr['subsidyPercentage'].setValue(null);
      }

      this.calculateSubsidyOptions(id);
      this.setValidators(fieldsRequired);
      this.removeValidators(fieldsRemoveValidators);
      this.formGroup.updateValueAndValidity({ onlySelf: true });
    });
    if (idHidden) this.hideAmountInput(idHidden);
  }

  calculateWarrantyPercentage(performCalculation: boolean = true) {
    const type = this.ctr['warrantyDeposit'].value;
    this.calculateWarrantyDepositTotal();
    return true;
  }

  calculateWarranty(id?: string) {
    this.onChangeWarrantyDepositValue();
    this.hideAmountInput(id);
  }

  changeBalloon(id?: string, idHidden?: string) {
    const type = this.ctr['balloonType'].value;
    this.validateAndExecute(() => {
      let fieldsRequired: string[] = [];
      let fieldsRemoveValidators: string[] = [];

      if (!type || type == 'null') {
        fieldsRemoveValidators = ['balloonPercentage', 'balloonType', 'balloon'];
        this.removeValidators(fieldsRemoveValidators);
        this.setValueAndErrors(fieldsRemoveValidators);
        this.ctr['balloon'].setValue(null);
        this.ctr['balloonPercentage'].setValue(null);
        this.ctr['balloonType'].setValue(null);
        return;
      }

      if (type == TaxType.invoiceWithTax || type == TaxType.invoiceWithoutTax || type == TaxType.amountToFinance) {
        fieldsRequired = ['balloonPercentage'];
        fieldsRemoveValidators = ['balloon'];

        this.ctr['balloon'].disable();
        this.ctr['balloonPercentage'].enable();
      } else if (type == TaxType.fixedAmount) {
        fieldsRequired = ['balloon'];
        fieldsRemoveValidators = ['balloonPercentage'];

        this.ctr['balloonPercentage'].disable();
        this.ctr['balloon'].enable();
      }

      this.calculateBalloonOptions(id);
      this.setValidators(fieldsRequired);
      this.removeValidators(fieldsRemoveValidators);
      this.formGroup.updateValueAndValidity({ onlySelf: true });
    });
    if (idHidden) this.hideAmountInput(idHidden);
  }

  changeResidualValue(id?: string, idHidden?: string) {
    const type = this.ctr['residualValueType'].value;
    this.validateAndExecute(() => {
      let fieldsRequired: string[] = [];
      let fieldsRemoveValidators: string[] = [];
      if (!type) {
        fieldsRequired = ['residualValuePercentage', 'residualValueType'];
      }

      if (
        type == this.taxType.invoiceWithTax ||
        type == this.taxType.invoiceWithoutTax ||
        type == this.taxType.amountToFinance
      ) {
        fieldsRequired = ['residualValuePercentage'];
        // fieldsRemoveValidators = ['residualValueTotal'];
        this.ctr['residualValueTotal'].disable();
        this.ctr['residualValuePercentage'].enable();
        if (!this.ctr['residualValuePercentage'].value)
          this.ctr['residualValuePercentage'].setErrors({
            required: true,
          });
        this.ctr['residualValueTotal'].setValue(null);
      } else if (type == this.taxType.fixedAmount) {
        fieldsRequired = ['residualValueTotal'];
        fieldsRemoveValidators = ['residualValuePercentage'];
        this.ctr['residualValuePercentage'].disable();
        this.ctr['residualValuePercentage'].setValue(null);
        this.ctr['residualValueTotal'].enable();
      }
      this.calculateResidualValueOption(id);

      this.setValidators(fieldsRequired);
      this.removeValidators(fieldsRemoveValidators);
      this.formGroup.updateValueAndValidity({ onlySelf: true });
    });
    if (idHidden) {
      this.hideAmountInput(idHidden);
    }
  }

  changePurchaseOption(id?: string, idHidden?: string) {
    const type = this.ctr['purchaseOptionType'].value;
    this.validateAndExecute(() => {
      let fieldsRequired: string[] = [];
      let fieldsRemoveValidators: string[] = [];

      if (
        type == this.taxType.invoiceWithTax ||
        type == this.taxType.invoiceWithoutTax ||
        type == this.taxType.amountToFinance
      ) {
        fieldsRequired = ['purchasePercentage'];
        //fieldsRemoveValidators = ['purchaseTotal'];
        this.ctr['purchaseTotal'].disable();
        this.ctr['purchasePercentage'].enable();
        if (!this.ctr['purchasePercentage'].value)
          this.ctr['purchasePercentage'].setErrors({
            required: true,
          });
        this.ctr['purchaseTotal'].setValue(null);
      } else if (type == this.taxType.fixedAmount) {
        //fieldsRequired = ['purchaseTotal'];
        fieldsRemoveValidators = ['purchasePercentage'];
        this.ctr['purchasePercentage'].disable();
        this.ctr['purchasePercentage'].setValue(null);
        this.ctr['purchaseTotal'].enable();
        // Al ENTRAR a monto fijo, purchaseTotal arrastra el resultado de
        // la base % (otra semántica): arranca vacío. En blur no se toca.
        if (this.previousFormGroup?.['purchaseOptionType'] !== type) {
          this.ctr['purchaseTotal'].setValue(null);
        }
      }

      // Si la base de cálculo es monto a financiar, recalcular el monto a financiar primero
      if (type == TaxType.amountToFinance) {
        this.setFinancialAmountCalculation();
      }
      // Monto fijo también precalcula el lado Con IVA del monto tipeado.
      this.onChangePurchaseOption(id);


      this.setValidators(fieldsRequired);
      this.removeValidators(fieldsRemoveValidators);
      this.formGroup.updateValueAndValidity({ onlySelf: true });
    });
    if (idHidden) {
      this.hideAmountInput(idHidden);
    }
  }

  calculateAdditionalCollateral() {
    const type = this.ctr['additionalCollateralType'].value;

    if (!type || type === 'null') {
      this.formGroup.patchValue({
        additionalCollateralPercentage: null,
        additionalCollateralTotal: null,
        additionalCollateralType: null,
      });
      return;
    }

    const percentage = this.ctr['additionalCollateralPercentage'].value;
    if (!percentage) {
      this.formGroup.patchValue({
        additionalCollateralTotal: null,
      });
      return;
    }

    const unitPrice = this.ctr['unitPriceWithoutTax'].value;
    const quantity = this.ctr['quantity'].value;

    if (type === 'UNIT_VALUE') {
      this.formGroup.patchValue({
        additionalCollateralTotal: (unitPrice * percentage) / 100,
      });
    } else if (type === 'TOTAL_VALUE') {
      this.formGroup.patchValue({
        additionalCollateralTotal: (unitPrice * quantity * percentage) / 100,
      });
    }
  }

  assignSpecialPayments(specialPayments: any) {
    this.ctr['specialPayments'].setValue(specialPayments);
  }

  cancel() {
    this.closeEdition.emit(true);
    this.cancelDiro();
  }

  cancelDiro() {
    const { calculateDiro, deal } = this.diroService.cancelDiroOperation(this._deal);
    if (calculateDiro) {
      this.formGroup.patchValue(deal);
      let data = {};
      this.calculateDiro(data);
    }
  }

  save() {
    const result = this.ctr['result'].value;
    if (this.formGroup.valid) {
      const warrantyDepositTotal = this.ctr['warrantyDepositTotal'].value;
      const unitPriceWithTax = this.ctr['unitPriceWithTax'].value;
      // if (warrantyDepositTotal > unitPriceWithTax) {
      //   this.showNotificationError('El monto del depósito en garantía es mayor al precio de la unidad con IVA.');
      // }
      if (result == null) {
        this.showNotificationError('Es necesario calcular para poder guardar.');
      } else {
        const deal = this.financialCalculator.getBodyforSaveDraft(this.formGroup, result);
        sessionStorage.removeItem('lastInput');
        this._deal = deal;
        this.update.emit(deal);
        this.closeEdition.emit(true);
      }
    } else {
      this.showNotificationError('Debe completar los campos requeridos para poder guardar.');
    }
  }

  assignProfitDeal(profitDeal: any) {
    this.ctr['profitDeal'].setValue(profitDeal);
  }

  deleteOpenQuotePayments(idDeal: number) {
    return this.quoteService.deletePaymentsDeal(this.idQuote, idDeal).pipe(take(1));
  }

  assignPayments(result: any) {
    this.payments = result.data[0].payments;
    this.quantityForPayments = this.ctr['quantity'].value;
    this.ctr['payments'].setValue(this.payments);
    this.quote = result.data;
    // Activo el bloqueo de actualizacion para evitar recalculos.
    // Cuando se realiza el recalculo, el ultimo paso es nullear el campo result.
    this.lockUpdate = true;
    this.ctr['result'].setValue({
      id: this.quote[0].id,
      creationDate: this.quote[0]?.creationDate,
      term: this.quote[0]?.term,
      unitPrice: this.quote[0]?.unitPrice,
      unitPriceNoTax: this.quote[0]?.unitPriceNoTax,
      annualPercentageRate: this.quote[0]?.annualPercentageRate,
      downpaymentAmount: this.quote[0]?.downpaymentAmount,
      openingfeeAmount: this.quote[0]?.openingfeeAmount,
      balloonAmount: this.quote[0].balloonAmount,
      guaranteeDepositAmount: this.quote[0].guaranteeDepositAmount,
      insuranceAmount: this.quote[0].insuranceAmount,
      additionalServicesAmount: this.quote[0].additionalServicesAmount,
      confirmationExpensesAmount: this.quote[0].confirmationExpensesAmount,
      initialPayment: this.quote[0].initialPayment,
      financialAmount: this.quote[0].financialAmount,
      monthlyPayment: this.quote[0].monthlyPayment,
      annualTotalCost: this.quote[0].annualTotalCost,
      purchaseOptionAmount: this.quote[0].purchaseOptionAmount,
      residualValueTotal: this.quote[0].residualValue,
    });
    this.loading.begin();
    this.ctr['downPayment'].setValue(this.quote[0].downpaymentAmount);

    // Monto fijo: purchaseOptionAmount viene Con IVA y el input es
    // Sin IVA; el precalc de abajo lo lleva a purchaseTotalWithTax.
    if (this.ctr['purchaseOptionType'].value !== TaxType.fixedAmount) {
      this.ctr['purchaseTotal'].setValue(this.quote[0].purchaseOptionAmount);
    }

    // La API regresa el residual Sin IVA en TODAS las bases de
    // calculo; el lado Con IVA lo deriva el precalc agregando IVA.
    this.ctr['residualValueTotal'].setValue(this.quote[0].residualValue);

    this.ctr['warrantyDepositTotal'].setValue(this.quote[0].guaranteeDepositAmount);
    this.ctr['balloon'].setValue(this.quote[0].balloonAmount);
    this.ctr['unitPriceWithoutTax'].setValue(this.quote[0].unitPriceNoTax);

    //financialAmount
    this.ctr['financialAmount'].setValue(this.quote[0].financialAmount);

    this.ctr['unitPriceWithoutTax'].setValue(this.quote[0].unitPriceNoTax);
    this.ctr['openingFeeWithTax'].setValue(this.quote[0].openingfeeAmount);

    if (this.isPurchaseOptionTypeNotNull) {
      this.calculatePurchaseOption('purchaseTotalWithTaxPrecalc');
    }
    if (this.isResidualValueTypeNotFixedAmount) {
      this.calculateResidualValueOption('residualValueTotalPrecalc');
    }

    const deal = this.financialCalculator.getBodyforSaveDraft(this.formGroup, this.ctr['result']?.value);

    // Actualizar campos a resaltar después de recibir respuesta de la API
    this.highlightFields = this.financialCalculator.getApiResultHighlight(this.formGroup);

    this._deal = deal;
    this.update.emit(deal);
    this.loading.end();
  }

  collapse() {
    this.collapsed = !this.collapsed;
    const element = document.getElementById('contract-edition') as HTMLInputElement;
    if (element.classList.contains('collapsed')) {
      element.classList.remove('collapsed');
    } else {
      element.classList.add('collapsed');
    }
  }

  setValidators(fields: string[]) {
    fields.map((field) => {
      const useValidator = [Validators.required];
      if (
        field === 'downPaymentTax' ||
        field === 'openingFeePercentage' ||
        field === 'warrantyDepositPercentage' ||
        field === 'subsidyPercentage' ||
        field === 'balloonPercentage' ||
        field === 'purchasePercentage' ||
        field === 'residualValuePercentage'
      ) {
        useValidator.push(Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/));
      }
      this.ctr[field].setValidators(useValidator);
      this.ctr[field].updateValueAndValidity();
    });
  }

  removeValidators(fields: string[]) {
    fields.map((field) => {
      this.ctr[field].clearValidators();
      this.ctr[field].updateValueAndValidity();
    });
  }

  setValueAndErrors(fields: string[]) {
    fields.map((field) => {
      this.ctr[field].setErrors(null);
      this.ctr[field].updateValueAndValidity();
    });
  }

  openModalConfirmation() {
    this.modalService.component = this.modalStatus()!;
    this.modalService.openModal();
  }

  deletePaymentsOutOfRange(type: 'irregularPayments' | 'specialPayments' | 'grace', term: number) {
    const value = this.ctr[type].value;
    const updatedValue = value.filter((item: any) => item.period <= term);
    const deletedItems = value.filter((item: any) => item.period > term);
    this.ctr[type].setValue(updatedValue);
    return deletedItems.length > 0 ? deletedItems : [];
  }

  confirmationByType() {
    const type = this.confirmationData().type;
    if (type === 'deletePaymentsOutOfRange') {
      this.confirmDeletePaymentsOutOfRange();
    } else if (type === 'editInsurance') {
      this.deleteInsurance();
    }
  }

  confirmDeletePaymentsOutOfRange() {
    const validatedTerm = this.ctr['term'].value;

    if (!validatedTerm || validatedTerm <= 0) {
      this.closeConfirmationModal();
      return;
    }
    const { hasIrregularGreaterThan, hasSpecialGreaterThan, hasGraceGreaterThan } = this.getSpecialPaymentsOutOfRange(validatedTerm);
    let paymentsDeleted = false;
    let graceDeleted: any[] = [];
    let specialDeleted: any[] = [];
    let irregularDeleted: any[] = [];

    if (hasIrregularGreaterThan) {
      irregularDeleted = this.deletePaymentsOutOfRange('irregularPayments', validatedTerm);
      paymentsDeleted = true;
    }

    if (hasSpecialGreaterThan) {
      specialDeleted = this.deletePaymentsOutOfRange('specialPayments', validatedTerm);
      paymentsDeleted = true;
    }

    if (hasGraceGreaterThan) {
      graceDeleted = this.deletePaymentsOutOfRange('grace', validatedTerm);
      paymentsDeleted = true;
      const currentGracePeriods = this.ctr['grace'].value;

      if (currentGracePeriods.length > 0 && currentGracePeriods.length >= validatedTerm) {
        const lastPeriodGrace = currentGracePeriods.find((g: any) => g.period === validatedTerm);

        if (lastPeriodGrace) {
          const updatedGrace = currentGracePeriods.filter((g: any) => g.period !== validatedTerm);
          this.ctr['grace'].setValue(updatedGrace);
          graceDeleted = [...graceDeleted, lastPeriodGrace];
        }
      }
    }
    this.closeConfirmationModal();
    if (paymentsDeleted) {
      this.loading.track(this.quoteService.deleteAllSpecialPaymentRegistered(irregularDeleted, specialDeleted, graceDeleted, this.idQuote, this.ctr['id'].value)).pipe(
        takeUntilDestroyed(this._destroyRef)
      ).subscribe({
        next: () => {
          this.calculateAndUpdateForm(paymentsDeleted);
        },
        error: (error) => {
          console.error('Error al eliminar pagos:', error);
          this.calculateAndUpdateForm(paymentsDeleted);
        }
      });
    } else {
      this.calculateAndUpdateForm(paymentsDeleted);
    }
  }

  calculateAndUpdateForm(updated: boolean) {
    if (updated) {
      this.formGroup.updateValueAndValidity();
    }
    this.calculateQuote('r');
  }

  openModalDeletePaymentConfirmation() {
    this.disableFormTemporarily();

    this.confirmationData.set({
      title: 'Confirmar para calcular',
      subtitle: 'Se eliminarán los pagos especiales y los periodos de gracia posteriores al plazo de la propuesta.',
      btn1: 'Cancelar',
      btn2: 'Confirmar',
      type: 'deletePaymentsOutOfRange',
    });

    this.modalService.component = this.modalConfirmation()!;
    this.modalService.openModal();

    if (document.body) {
      document.body.style.overflow = 'hidden';
    }
  }

  onChangeClassification() {
    const classificationType = this.ctr['classification'].value;
    if (classificationType == 'null') this.ctr['classification'].setValue(null);
    if (this.isActiveBrokerCatalog) {
      // Forzar la limpieza de datos y la recarga
      this.cleanModelData();
      this.fetchDataIfChangeModelOrClassification();
      this._filteredData.update((curr) => ({
        ...curr,
        classification: classificationType == 'NEW' ? true : false,
        year: '',
      }));
      if (!this.isAccesoriesProduct) {
        this.ctr['year'].setValue('');
      }
      this.insuranceService.resetForm.next();
    } else {
      this.ctr['modelId'].setValidators(null);
      this.ctr['year'].setValidators(null);
      this.ctr['modelId'].setValue(null);
      this.ctr['year'].setValue(null);
      if (!Boolean(classificationType)) {
        this.ctr['classification'].setValue(null);
        this.ctr['classification'].setErrors(null);
        this.ctr['classification'].setValidators(null);
        return;
      }
    }
    this.setUseTimeValidators();
  }
  getClassificationForAccesories() {
    const year = this.ctr['year'].value;
    if (!year) {
      return null;
    }
    const now = new Date();
    const nowYear = now.getFullYear();
    const nowMonth = now.getMonth() + 1;
    const isCutUsedModels = nowMonth >= 10;
    const usedModelYears = isCutUsedModels ? nowYear - 1 : nowYear - 2;
    if (year <= usedModelYears) {
      return 'USED';
    }
    return 'NEW';
  }
  getClassificationOptions() {
    const classification = this.getClassificationForAccesories();
    if (classification === null) {
      return "";
    }
    if (classification === 'USED') {
      return "Used";
    }
    return "New";
  }

  setUseTimeValidators() {
    const classificationType = this.ctr['classification'].value;
    if (classificationType === true || classificationType === 'NEW') {
      this.ctr['useTime'].setValue(null);
      this.ctr['useTime'].setValidators(null);
      this.ctr['useTime'].disable();
    } else if (classificationType === false || classificationType === 'USED') {
      this.ctr['useTime'].setValidators([Validators.required]);
      this.ctr['useTime'].enable();
    } else if (classificationType === null) {
      this.ctr['useTime'].setValue(null);
      this.ctr['useTime'].setValidators(null);
      this.ctr['useTime'].disable();
    }
    this.formGroup.get('useTime')?.updateValueAndValidity();
  }

  getTerm() {
    const insuranceDeals = this.ctr['insuranceDeals'].value;
    let termInsurance: number[] = [];

    if (insuranceDeals.length > 0) {
      insuranceDeals.map((i: any) => termInsurance.push(i.term));
      this.termMin = Math.min(...termInsurance);
      this.term = `Meses (min. ${this.termMin})`;
    } else {
      this.term = 'Meses';
    }
  }


  onChangeTerm() {
    this.validateAndExecute(() => {
      const term = this.ctr['term'].value;
      const insuranceDeals = this.ctr['insuranceDeals'].value;
      if ((!term && term !== 0) || !insuranceDeals) return;
      if (term < this.termMin) {
        this.ctr['term'].setValue(null);
      }
      this.minMaxTermBusinessRules();

    });
    this.updateInsuranceData();
  }

  updateGrace(grace: any[]): void {
    this.ctr['grace'].setValue(grace);

    this.updateInsuranceData();
    this.resetMarshAndManualInsurancePayments();
    this.calculateQuote('r');
  }

  updateSpecialPayments(specialPayments: any[]): void {
    this.ctr['specialPayments'].setValue(specialPayments);
    this.formGroup.updateValueAndValidity();
    this.calculateQuote('r');
  }

  updateIrregularPayments(irregularPayments: any[]): void {
    this.ctr['irregularPayments'].setValue(irregularPayments);
    this.calculateQuote('r');
  }

  selectSection(index: number) {
    this.showSection = index;
  }

  deleteAllInsurance() {
    this.modalService.closeModal();
    this.loading.track(this.quoteService.deleteAllInsurance(this.idQuote, this._deal.id))
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.ctr['insuranceDeals'].setValue([]);
          const deal = this.financialCalculator.getBodyforSaveDraft(this.formGroup);
          this._deal = deal;
          this.update.emit(deal);
        }
      });
  }

  calculateQuoteToDownloadJson() {
    let model = this.financialCalculator.getBodyforCalculateQuote(this.formGroup, this.nameCompany());
    const data = this.cleanModelCalculatedData(model);
    this.applyLifeInsuranceCalculatePayload(data, this.ctr['insurance'].value);

    this.loading.track(this.quoteService.calculateQuoteOpenJson(data)).subscribe({
      next: (response) => {
        //timeout
        if (response.status === true) {
          this.loading.begin();
          const jsonStr = JSON.stringify(response.data, null, 2);

          const cotizacionFileName = this.idQuote ? `Cotizacion(${this.idQuote}).json` : 'Cotizacion.json';

          const blob = new Blob([jsonStr], { type: 'application/json' });

          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = cotizacionFileName;
          document.body.appendChild(a);
          a.click();

          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          this.loading.end();
        }
      },
      error: () => {
        this.showNotificationError(
          'Ha ocurrido un error al momento de realizar la descarga del json del calculo. Por favor, intente nuevamente o solicite soporte.'
        );
      },
    });
  }

  deleteAllSpecialPayments() {
    this.modalService.closeModal();
    this.loading.track(this.quoteService.deleteAllSpecialPayments(this.idQuote, this._deal.id))
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.ctr['specialPayments'].setValue([]);
          this.deleteAllGracePeriod();
        }
      });
  }

  deleteAllGracePeriod() {
    this.modalService.closeModal();
    this.loading.track(this.quoteService.deleteAllGracePeriod(this.idQuote, this._deal.id))
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.ctr['grace'].setValue([]);
          const deal = this.financialCalculator.getBodyforSaveDraft(this.formGroup);
          this._deal = deal;
          this.update.emit(deal);
        }
      });
  }

  isLifeInsuranceApplicable(): boolean {
    return isLifeInsuranceEligible(
      this.customer?.personType,
      this.ctr['financialProductType'].value,
    );
  }

  isNaturalPerson() {
    return this.customer.personType === 'NATURAL_PERSON';
  }

  // Nuevas funciones para el catalogo
  fetchDataIfChangeModelOrClassification(callback?: () => void) {
    const productType = this.ctr['productType'].value;
    const classification = this.ctr['classification'].value;
    const isNewClassification = classification === 'NEW';
    if (productType && this.isActiveBrokerCatalog) {
      const sendClassification = this.isAccesoriesProduct && this.isActiveAccesoriesMarsh ? null : isNewClassification;
      this.cleanModelData();
      this.fetchData(productType, sendClassification).add(() => {
        if (callback) {
          callback();
        }
      });
    }
  }

  fetchData(modelType: string, classification: boolean | null) {
    return this.loading.track(this.quoteService.getModelsByFilter({ modelType, classification }))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.allModels.set(response.data);
        },
      });
  }

  cleanModelData() {
    if (this.isStarting() || !this.isActiveBrokerCatalog) {
      return;
    }
    const classification = this.isAccesoriesProduct ? this.getClassificationForAccesories() : this.ctr['classification'].value;
    this.ctr['modelId'].setValue(null);
    // this.ctr['quantity'].setValue(null);
    this.ctr['brandId'].setValue(null);
    this.ctr['subBrandId'].setValue(null);
    this.ctr['unitPriceWithTax'].setValue(null);
    this.ctr['unitPriceWithoutTax'].setValue(null);
    this.ctr['totalPriceWithTax'].setValue(null);
    this.ctr['totalPriceWithoutTax'].setValue(null);
    this._selectedModel = null;
    this._filteredData.set({
      productType: this.ctr['productType'].value,
      classification: classification === null ? null : classification === 'NEW',
      year: this.ctr['year'].value || '',
    });
  }

  getImageBySubrandOrProduct(product: string, subBrandId: number) {
    if (!!subBrandId && subBrandId !== -1) {
      this.getFile(subBrandId, product);
    } else {
      this.setDefaultImage(product);
    }
  }

  defineSubBrandImage(product: string, subBrandId: number) {
    if (!this.isActiveBrokerCatalog && this.showImages.productType !== product) {
      if (product === productTypeEnum.autobuses) {
        if (this.validationsService.hasFeatureWithDefaultText(this.ctr['feature'].value)) this.ctr['feature'].setValue('Mercedes Benz');
        this.ctr['brandId'].setValue(1);
        this.ctr['subBrandId'].setValue(null);
        this.formGroup.get('subBrandId')?.setValue(null);
      } else if (product === productTypeEnum.camiones || product === productTypeEnum.tractos) {
        if (this.validationsService.hasFeatureWithDefaultText(this.ctr['feature'].value)) this.ctr['feature'].setValue('Freightliner');
        this.ctr['brandId'].setValue(2);
        this.ctr['subBrandId'].setValue(null);
        this.formGroup.get('subBrandId')?.setValue(null);
      } else if (product === productTypeEnum.accesorios) {
        if (this.validationsService.hasFeatureWithDefaultText(this.ctr['feature'].value)) this.ctr['feature'].setValue('Accesorios');
      } else {
        if (this.validationsService.hasFeatureWithDefaultText(this.ctr['feature'].value)) this.ctr['feature'].setValue(' ');
        this.showImages.productType = product;
        this.formGroup.get('brandId')?.reset();
        this.ctr['subBrandId'].setValue(null);
        this.formGroup.get('subBrandId')?.setValue(null);
        subBrandId = -1;
      }
    }
    this.getImageBySubrandOrProduct(product, subBrandId);
  }

  changeModel(event: any) {
    const modelId = this.ctr['modelId'].value;
    const modelSelected: any = this.allModels().find((i) => i.id == modelId);
    if (!modelId || !event || !modelSelected) return;

    this._selectedModel = modelSelected;

    this.quoteService.residualValue = modelSelected?.residualModelValues ?? [];
    const productType = this.ctr['productType'].value;
    const price = this._selectedModel?.price;
    const parsedPrice = this.financialCalculator.round2(price);
    this.formGroup.patchValue({
      brandId: this._selectedModel?.subBrand?.brand?.id,
      subBrandId: this._selectedModel?.subBrand?.id,
      year: this._selectedModel?.year,
    });
    if (parsedPrice > 0) {
      this.ctr['unitPriceWithTax'].setValue(this._selectedModel?.price);
    }
    this._filteredData.update((curr) => ({
      ...curr,
      productType: productType,
      year: this._selectedModel?.year,
    }));

    if (this._originalSubBrand != this._selectedModel?.subBrand?.id) {
      this.ctr['subBrandUrl'].setValue(null);
      this.showImages.hasSubbrandImg = false;
      this._originalSubBrand = this._selectedModel?.subBrand?.id;
      this.getImageBySubrandOrProduct(productType, this._selectedModel?.subBrand?.id);
    }
    this.calculateUnitPriceWithTax('unitPriceWithTax');
  }

  changeYear(event: any) {
    const yearValue = event.target.value;

    if (this.isActiveBrokerCatalog) {
      if (this.isAccesoriesProduct && this.isActiveAccesoriesMarsh) {
        const updatedClassification = this.getClassificationForAccesories();
        this.ctr['classification'].setValue(updatedClassification);
        this.ctr['classification'].updateValueAndValidity({ onlySelf: true });
        this.setUseTimeValidators();
      }
      this.validateUseTime();
      const isYearSignalValid = !!this._years();
      const isNewYearDifferentFromSelectedModel = !this._selectedModel || this._selectedModel.year != yearValue;
      if (isYearSignalValid && isNewYearDifferentFromSelectedModel) {
        this.cleanModelData();
      }
      this.updateInsuranceData();
      this._years.set(yearValue);
      this._filteredData.update((curr) => ({ ...curr, year: yearValue }));
    }
  }

  onChangeIncludesInsurance(event: any) {
    const newValue = event.target.checked;
    if (newValue) {
      if (this.ctr['productType'].value == productTypeEnum.vanes) {
        this.ctr['productType'].setValue(null);
      }
      // Se activa los valores para el catalogo
      this.ctr['modelId'].setValidators(Validators.required);
      this.ctr['year'].setValidators(Validators.required);
      this.ctr['modelId'].enable();
      this.ctr['year'].enable();
      this.ctr['classification'].setValue('NEW');
      this.fetchDataIfChangeModelOrClassification();
      this.ctr['insurance'].enable();
    } else {
      this.insuranceService.resetForm.next();
      this.ctr['insurance'].disable();
      this.setBrandAndSubBrand()?.add(() => {
        this.ctr['modelId'].setValue(null);
        this.ctr['year'].setValue(null);
        this.ctr['classification'].setValue(null);
        this.ctr['modelId'].disable();
        this.ctr['year'].disable();
      });
    }
    this.formGroup.updateValueAndValidity();
  }

  notExecuteAction(): boolean {
    const insurance = this.ctr['insurance'].value;
    const isEditInsurance =
      insurance !== null &&
      insurance !== undefined &&
      insurance?.creationDate !== null &&
      insurance?.creationDate !== undefined;
    return this.isActiveBrokerCatalog && isEditInsurance && !this.isStarting();
  }

  verifyInsurance(nativeEl: HTMLElement, target: FocusEvent, fieldName: string) {
    if (this.notExecuteAction()) {
      // Prevenir inmediatamente cualquier acción del evento
      target?.preventDefault?.();
      target?.stopPropagation?.();
      target?.stopImmediatePropagation?.();

      // Deshabilitar temporalmente todo el formulario
      this.disableFormTemporarily();

      // Asegurar que el elemento pierde completamente el foco
      this.removeFocusFromElement(nativeEl);

      this._focusElement = nativeEl;
      this._fieldName = fieldName;

      // Configurar datos del modal de confirmación
      this.confirmationData.set({
        title: 'Editar propuesta',
        subtitle: '¿Está seguro de querer editar la propuesta, esto eliminara el seguro?',
        btn1: 'Cancelar',
        btn2: 'Editar',
        type: 'editInsurance',
      });

      // Abrir modal inmediatamente y bloquear scroll
      this.modalService.component = this.modalConfirmation()!;
      this.modalService.openModal();

      // Bloquear scroll del body
      if (document.body) {
        document.body.style.overflow = 'hidden';
      }
    }
    // Si no hay seguro editado, no hacer nada y permitir que el evento continúe normalmente
  }

  closeConfirmationModal() {
    this.modalService.closeModal();
    this.enableFormAndRestoreState();
  }

  deleteInsurance() {
    this.modalService.closeModal();
    this.insuranceService.resetForm.next();
    this.ctr['result'].setValue(null);
    this.enableFormAndRestoreState();
  }

  canAddInsurance() {
    return this.ctr['includesInsurance']?.value || false;
  }

  resetInsurance(e: any) {
    if (this.isStarting()) return;
    this.ctr['insurance']?.setValue?.(null);
    this.formGroup.updateValueAndValidity({ onlySelf: true });
  }

  isModelValid(): boolean {
    const model = this.insuranceService.insuranceModel();
    const isAccesories = model?.modelType === productTypeEnum.accesorios;
    if (isAccesories && !environment.active_accesories_marsh) {
      return false;
    }
    return !!model && !!model.modelCode && !!model.modelType;
  }

  isFormValidExceptInsurance(): boolean {
    if (this.formGroup.valid) return true;
    const formErrors = this.getFormValidationErrors();
    return formErrors.length === 0 || (formErrors.length === 1 && formErrors[0] === 'insurance');
  }

  isValid(): boolean {
    const modelValid = this.isModelValid();
    const paramsValid = this.isInsuranceParamsValid();
    const formValid = this.formGroup.valid;
    return modelValid && paramsValid && formValid;
  }

  updateInsuranceData(): void {
    if (!this.isActiveBrokerCatalog) return;
    // Valores para commonInsuranceParams
    const financialProduct = this.ctr['financialProductType']?.value;
    const isAF = financialProduct == FinancialProductEnum.financialLeasing;
    const isCdBalloon = financialProduct == FinancialProductEnum.directCreditGlobe;
    const commonInsuranceUpdates = {
      companyName: this.nameCompany?.() || this.financialService?.companyName,
      financialProduct: isAF || isCdBalloon ? FinancialProductEnum.directCredit : financialProduct,
      term: this.ctr['term']?.value,
      customer: {
        creationDate: null,
        creationUser: null,
        firstName: this?.customer?.name,
        id: this?.customer?.id,
        lastName: this?.customer?.lastName,
        mothersLastName: this?.customer?.mothersLastName,
        personType: this?.customer?.personType,
        secondName: null,
        updateUser: null,
        updatedDate: null,
      },
      dealerBranchId: this.insuranceService.dealerBranchId(),
      dealerId: this.insuranceService.dealerId(),
      oldId: 0,

      currency: this.ctr['currencyType']?.value,
      paymentType: this.ctr['paymentType']?.value,
      quantity: this.ctr['quantity']?.value,
      taxFree: this.ctr['taxFree']?.value,
      customerRate: this.ctr['customerRate']?.value,
      paymentFormationType: this.ctr['paymentFormationType']?.value,
      openingFeeType: this.ctr['openingFeeType']?.value,
      openingFeePercentage: this.ctr['openingFeePercentage']?.value,
      openingFee: this.ctr['openingFee']?.value,
      downPaymentTaxType: this.ctr['downPaymentTaxType']?.value,
      downPaymentTax: this.ctr['downPaymentTax']?.value,
      downPayment: 0,
      lifeInsuranceFactor: this.ctr['lifeInsuranceFactor']?.value,
      confirmationExpensesAmount: this.ctr['confirmationExpensesAmount']?.value,
      grace: this.ctr['grace']?.value,
      residualValueType: this.ctr['residualValueType']?.value,
      residualValuePercentage: this.ctr['residualValuePercentage']?.value,
      residualValueTotal: this.ctr['residualValueTotal']?.value,
    };

    // Valores para modelInsurance
    const model = this._selectedModel;
    const openQuotePrice = this.ctr['unitPriceWithTax']?.value;
    const productType = this.ctr['productType']?.value ?? "";
    const classification = this.isAccesoriesProduct ? this.ctr['classification'].value === "NEW" : this.ctr['classification'].value === "NEW";
    const modelInsuranceUpdates = {
      classification: classification,
      modelType: productType,
      modelCode: model?.modelCode || '',
      name: model?.name || '',
      year: model?.year || '',
      price: openQuotePrice ? Number(openQuotePrice) : Number(model?.price),
      subBrand: { name: model?.subBrand?.name || '' },
    };

    this.insuranceService.insuranceParams.update((before) =>
      this.insuranceService.updateMultipleProperties(before, commonInsuranceUpdates)
    );
    this.insuranceService.insuranceModel.update((before) =>
      this.insuranceService.updateMultipleProperties(before, modelInsuranceUpdates)
    );
  }

  updateLastChangesForFormGroup() {
    this.previousFormGroup = this.formGroup.getRawValue();
  }

  hasFormGroupChanged(): boolean {
    // Si previousFormGroup no está inicializado, inicializarlo y retornar false
    if (!this.previousFormGroup) {
      this.previousFormGroup = this.formGroup.getRawValue();
      return true;
    }
    const keysToCompare = KEYS_TO_COMPARE_FOR_UPDATES;

    return keysToCompare.some((key) => {
      const current = this.formGroup.get(key)?.value;
      const previous = this.previousFormGroup?.[key];
      const isCurrentEmpty = current === null || current === undefined || current === '';
      const isPreviousEmpty = previous === null || previous === undefined || previous === '';
      const compare = isCurrentEmpty && isPreviousEmpty ? false : current !== previous;
      return compare;
    });
  }

  validateAndExecute<T>(callback: () => void) {
    if (this.hasFormGroupChanged() && !this.lockUpdate) {
      callback();
      this.updateLastChangesForFormGroup();
      this.ctr['result'].setValue(null);
      // Resetear campos de resaltado cuando se borra el resultado
      this.highlightFields = this.financialCalculator.emptyHighlightFields;
      // Resetear todos los signals de diferencia con API
      this.resetAllAPIDifferenceSignals();
    }
  }

  onChangeCustomerRate() {
    this.validateAndExecute(() => {
      this.resetMarshAndManualInsurancePayments();
    });
  }

  onChangeCurrencyType() {
    this.validateAndExecute(() => {
      this.resetMarshAndManualInsurancePayments();
      const results = this.financialCalculator.convertCurrency(this.formGroup);
      Object.entries(results).forEach(([key, value]) => {
        this.ctr[key].setValue(value);
      });
      this.calculateTotal();
    });
  }

  onlyChangeValue(id?: string) {
    this.validateAndExecute(() => { });
    if (id) {
      this.hideAmountInput(id);
    }
  }

  validateUseTime() {
    const useTime = this.ctr['useTime'].value;
    const currentYear = this.ctr['year'].value;
    const classification = this.ctr['classification'].value;
    const isUsedClassification = classification === false || classification === 'USED';
    if (useTime && currentYear && isUsedClassification) {
      const validation = this.validationsService.validateUseTime(useTime, currentYear);
      const state = this.validationsService.handleUseTimeValidation(validation);
      if (!state.success) {
        this.ctr['useTime'].setErrors({ 'useTime': state.message });
        this.ctr['useTime'].setValidators([Validators.required, Validators.max(validation.maxUseTime)]);
        this.showNotificationError(state.message);
      } else {
        this.ctr['useTime'].setErrors(null);
      }
      this.ctr['useTime'].updateValueAndValidity();
    }
  }

  onCHnageUseTime() {
    this.validateAndExecute(() => {
      this.validateUseTime();
    });
  }

  onChangeWarrantyDepositSelectorValue(id: string) {
    this.validateAndExecute(() => {
      this.setWarrantyValidate();
      this.calculateWarrantyPercentage();
    });
    this.hideAmountInput(id);
  }

  onChangeWarrantyDepositValue() {
    this.validateAndExecute(() => {
      this.calculateWarrantyPercentage();
      this.formGroup.updateValueAndValidity({ onlySelf: true });
    });
  }

  setWarrantyValidate() {
    const type = this.ctr['warrantyDeposit'].value;
    let fieldsRequired: string[] = [];
    let fieldsRemoveValidators: string[] = [];

    if (!type || type === 'null') {
      this.ctr['warrantyDeposit'].setValue(null);
      this.ctr['warrantyDepositTotal'].setValue(null);
      this.ctr['warrantyDepositPercentage'].setValue(null);
      this.ctr['warrantyDepositMonth'].setValue(null);
      return;
    }

    if (type == this.taxType.invoiceWithTax || type == this.taxType.invoiceWithoutTax) {
      this.ctr['warrantyDepositTotal'].disable();
      this.ctr['warrantyDepositTotal'].setValue(null);

      this.ctr['warrantyDepositMonth'].disable();
      this.ctr['warrantyDepositMonth'].setValue(null);

      this.ctr['warrantyDepositPercentage'].enable();
    } else if (type == this.taxType.fixedAmount) {
      this.ctr['warrantyDepositPercentage'].disable();
      this.ctr['warrantyDepositPercentage'].setValue(null);

      this.ctr['warrantyDepositMonth'].disable();
      this.ctr['warrantyDepositMonth'].setValue(null);

      this.ctr['warrantyDepositTotal'].enable();
    } else if (type === this.taxType.numberOfRents) {
      this.ctr['warrantyDepositPercentage'].disable();
      this.ctr['warrantyDepositTotal'].disable();

      this.ctr['warrantyDepositPercentage'].setValue(0);
      this.ctr['warrantyDepositTotal'].setValue(0);
      this.ctr['warrantyDepositMonth'].enable();
    }

    this.formGroup.updateValueAndValidity({ onlySelf: true });
  }

  onChangePurchaseOption(id?: string) {
    this.calculatePurchaseOption(id);
  }

  calculatePurchaseOption(id = 'purchaseTotalWithTax') {
    if (this.ctr['purchaseOptionType'].value === TaxType.fixedAmount) {
      this.syncPurchaseFixedAmountWithTax(id);
      return;
    }
    const config: CalculationConfig = {
      typeControl: 'purchaseOptionType',
      defaultId: 'purchaseTotalWithTax',
      withoutTaxId: 'purchaseTotalWithoutTax',
      precalcId: 'purchaseTotalWithTaxPrecalc',
      sourceControl: 'purchaseTotal',
      roundingDecimals: 2,
      getFields: (type) => this.financialCalculator.getPurchaseFields(type),
      calculateMethod: (form) => this.financialCalculator.calculatePurchase(form),
      precalcPrimarySource: () =>
        this.financialCalculator.resolvePurchasePrecalcPrimary(this.formGroup),
    };
    this.calculateOptions(config, id);
  }

  /**
   * Monto fijo: purchaseTotal es lo que tipea el usuario (Sin IVA) y
   * NO se pisa. Solo se escribe purchaseTotalWithTax: en precalc
   * (load / post-Calcular) manda la API, en blur se deriva local.
   */
  private syncPurchaseFixedAmountWithTax(id: string) {
    const preferApi = id === 'purchaseTotalWithTaxPrecalc';
    const withTax = this.financialCalculator.resolvePurchaseFixedAmountWithTax(
      this.formGroup,
      preferApi
    );
    // Solo en precalc hay un resultado de API vigente que comparar.
    // En blur el usuario retipeó: el eco de la API quedó stale y el
    // lado Con IVA vuelve a la derivación local, sin discrepancia.
    this.hasPurchaseFixedAmountDifferentFromAPI.set(
      preferApi &&
        this.financialCalculator.hasPurchaseFixedAmountApiMismatch(
          this.formGroup
        )
    );
    this.ctr['purchaseTotalWithTax'].setValue(withTax);
  }

  calculateResidualValueOption(id = 'residualValueTotal') {
    const config: CalculationConfig = {
      typeControl: 'residualValueType',
      defaultId: 'residualValueTotal',
      withoutTaxId: 'residualValueTotalWithoutTax',
      precalcId: 'residualValueTotalPrecalc',
      sourceControl: 'residualValueTotal',
      primaryIsWithoutTax: true,
      getFields: (type) => this.financialCalculator.getResidualFields(type),
      calculateMethod: (form) => this.financialCalculator.calculateResidualValue(form),
    };
    this.calculateOptions(config, id);
  }

  calculateSubsidyOptions(id = 'subsidyTotalWithTax') {
    const config: CalculationConfig = {
      typeControl: 'subsidyType',
      defaultId: 'subsidyTotalWithTax',
      withoutTaxId: 'subsidyTotal',
      precalcId: 'subsidyTotalWithTaxPrecalc',
      sourceControl: 'subsidyTotal',
      getFields: (type) => this.financialCalculator.getSubsidyFields(type),
      calculateMethod: (form) => this.financialCalculator.calculateSubsidy(form),
    };
    this.calculateOptions(config, id);
  }

  calculateBalloonOptions(id = 'balloonTotalWithTax') {
    const config: CalculationConfig = {
      typeControl: 'balloonType',
      defaultId: 'balloonTotalWithTax',
      withoutTaxId: 'balloonTotal',
      precalcId: 'balloonTotalWithTaxPrecalc',
      sourceControl: 'balloon',
      getFields: (type) => this.financialCalculator.getBalloonFields(type),
      calculateMethod: (form) => this.financialCalculator.calculateBalloonAmount(form),
    };
    this.calculateOptions(config, id);
  }

  resetMarshAndManualInsurancePayments() {
    const insuranceDeals: any[] = this.ctr['insuranceDeals'].value || [];

    if (
      insuranceDeals &&
      insuranceDeals?.length > 0 &&
      insuranceDeals.some((insuranceDeal: any) => insuranceDeal?.payments != null)
    ) {
      this.reCalculateInsuranceDealPayments = true;
    }
    if (this.isAvailableInsurancePayments) {
      this.insuranceService.reCalculateInsuranceAmortization = true;
    }
  }

  deleteManualInsurancePayments() {
    const insuranceDeals: any[] = this.ctr['insuranceDeals'].value || [];
    const newInsurance = insuranceDeals.map((insuranceDeal: any) => ({
      ...insuranceDeal,
      grace: null,
      payments: null,
      result: null,
    }));
    const id = this.ctr['id'].value;
    this.ctr['insuranceDeals'].setValue(newInsurance);
    const deleteOperations = newInsurance.map((insuranceDeal: any) =>
      this.quoteService
        .deletePaymentsInsurance(this.idQuote, id, insuranceDeal.id)
        .pipe(takeUntilDestroyed(this._destroyRef))
    );
    return this.loading.track(forkJoin(deleteOperations))
  }

  getModelNameWithCode(model: any) {
    const codePart = model?.modelCode ?? '';
    const pattern = new RegExp(`\\|\\s*MB.*$`);
    const exactlyPattern = new RegExp(`\\s*${codePart}\\s*$`);

    let labelWithoutCode = model?.label?.replace?.(pattern, '')?.trim?.() ?? '';
    labelWithoutCode = labelWithoutCode.replace(exactlyPattern, '');
    return `${labelWithoutCode} | ${codePart}`;
  }

  // START PRIVATE

  private calculatePureFinancialAmount(): number {
    return Number(this.financialCalculator.calculateFinancialAmount(this.formGroup)) || 0;
  }

  private determineFinancialAmountValue(calculatedAmount: number): { finalValue: number; hasApiResult: boolean } {
    const apiResult = this.ctr['result'].value?.financialAmount;
    const hasApiResult = apiResult != null && apiResult != undefined;

    // Verificar diferencias entre API y calculado
    this.verifyDifferenceBetweenAPIAndCalculated(apiResult, calculatedAmount, 'hasFinancialAmountDifferentFromAPI');

    // Siempre usar valor de API cuando está disponible, independientemente de si hay diferencia significativa
    const finalValue = hasApiResult ? apiResult : calculatedAmount;

    return { finalValue, hasApiResult };
  }

  private executeProductSpecificCalculations(financialProduct: string): void {
    if (
      financialProduct == FinancialProductEnum.directCredit ||
      financialProduct == FinancialProductEnum.directCreditGlobe
    ) {
      this.calculateAdditionalCollateral();
    } else if (financialProduct == FinancialProductEnum.financialLeasing) {
      this.calculatePurchaseOption('purchaseTotalWithTax');
    } else if (financialProduct == FinancialProductEnum.pureLease) {
      this.calculateResidualValueOption('residualValueTotal');
    }
  }

  private executeFinalCalculations(): void {
    this.performCommissionCalculationForOpening();
    this.calculateSubsidyOptions('subsidyTotalWithTax');
  }

  private calculateAndSetPriceWithTax(unitPrice: number): void {
    const tax = parseInt(this.ctr['tax'].value);
    const taxDecimal = tax / 100;

    const unitPriceValue = parseFloat(unitPrice.toString());
    const unitPriceWithTax = unitPriceValue * (1 + taxDecimal);
    const roundedUnitPriceWithTax = this.financialCalculator.round2(unitPriceWithTax);

    this.ctr['unitPriceWithTax'].setValue(roundedUnitPriceWithTax);
    this.ctr['unitPriceWithoutTax'].setValue(this.financialCalculator.round2(unitPriceValue));

    this.calculateTotal();
  }

  private isValidTaxValue(tax: any): boolean {
    const taxRegex = /^(?:[0-9]{1,2}(?:\.[0-9]{1,4})?)$/;

    return taxRegex.test(tax);
  }

  private resetPriceRelatedFields(unitPrice: any, id: string): void {
    const fieldsToReset = ['totalPriceWithoutTax', 'totalPriceWithTax', 'downPayment', 'financialAmount'];
    if (id == 'unitPriceWithoutTax') {
      fieldsToReset.push('unitPriceWithTax');
    } else if (id == 'unitPriceWithTax') {
      fieldsToReset.push('unitPriceWithoutTax');
    }

    fieldsToReset.forEach((field) => {
      this.ctr[field].setValue(unitPrice);
    });
  }

  private calculateAndSetPriceWithoutTax(unitPrice: number, tax: any): void {
    const taxDecimal = parseInt(tax) / 100;

    const roundedUnitPriceWithoutTax = this.financialCalculator.calculateWithoutIVA(unitPrice, taxDecimal);

    this.ctr['unitPriceWithoutTax'].setValue(roundedUnitPriceWithoutTax);
    this.ctr['unitPriceWithTax'].setValue(this.financialCalculator.round2(unitPrice));

    this.calculateTotal();
  }

  private executePostCalculationUpdates(): void {
    if (!this.isFinancialProductTypePureLease) {
      this.setDownPaymentCalculation(this.ctr['downPaymentTaxType'].value);
      this.calculateWarrantyPercentage();
    }
    this.setFinancialAmountCalculation();
    this.performCommissionCalculationForOpening(true);
    this.calculateSubsidyOptions();
    this.calculatePurchaseOption();
    this.calculateResidualValueOption();
  }

  private calculateOptions(config: CalculationConfig, id: string) {
    const type = this.ctr[config.typeControl].value;
    if (!type || type === 'null' || type === null) return;

    const { withTax, withoutTax } = config.getFields(type);
    const tax = this.ctr['tax'].value;
    const taxDecimal = Number(parseFloat(tax) / 100);
    // Conceptos cuyos campos no se invierten (valor residual): el
    // primario es SIEMPRE el lado Sin IVA, asi que la direccion del
    // IVA se fuerza a "agregar", nunca a "quitar".
    const taxDirectionType = config.primaryIsWithoutTax
      ? TaxType.invoiceWithoutTax
      : type;

    if (id === config.withoutTaxId) {
      const valueWithTax = this.ctr[withTax].value;
      const roundedTotalWithoutTax = this.financialCalculator.calculateWithoutIVA(
        valueWithTax,
        taxDecimal,
        config.roundingDecimals || 0
      );
      this.ctr[withoutTax].setValue(roundedTotalWithoutTax);
    } else if (id === config.defaultId) {
      const calculatedRounded = config.calculateMethod(this.formGroup);

      const additional = this.financialCalculator.calculateAdditional(
        taxDirectionType,
        calculatedRounded,
        tax
      );
      this.setTaxValues(
        taxDirectionType,
        withTax,
        withoutTax,
        calculatedRounded,
        additional
      );
    } else if (id === config.precalcId) {
      const calculatedValue = config.precalcPrimarySource
        ? config.precalcPrimarySource()
        : this.ctr[config.sourceControl].value;
      let additional: any = 0;

      if (config.customPrecalcLogic) {
        additional = config.customPrecalcLogic(calculatedValue, taxDecimal, this.financialCalculator);
      } else {
        additional = this.financialCalculator.calculateAdditional(
          taxDirectionType,
          calculatedValue,
          tax
        );
      }

      this.setTaxValues(
        taxDirectionType,
        withTax,
        withoutTax,
        calculatedValue,
        additional
      );
    }
  }

  private setTaxValues(type: string, withTax: string, withoutTax: string, primary: any, additional: any) {
    if (type === TaxType.invoiceWithTax) {
      this.ctr[withTax].setValue(primary);
      this.ctr[withoutTax].setValue(additional);
    } else if (type === TaxType.invoiceWithoutTax || type === TaxType.amountToFinance || type === TaxType.fixedAmount) {
      this.ctr[withoutTax].setValue(primary);
      this.ctr[withTax].setValue(additional);
      // this.ctr[withTax].setValue(additional);
    }
  }

  private removeFocusFromElement(element: HTMLElement): void {
    if (!element) return;

    // Hacer blur del elemento
    element.blur();

    // Si es un select, asegurar que se cierre
    if (element.tagName?.toLowerCase() === 'select') {
      element.blur();
      // Forzar que pierda el foco
      (element as HTMLSelectElement).blur();
    }

    // Remover focus de cualquier elemento activo en el documento
    if (document.activeElement && document.activeElement !== document.body) {
      (document.activeElement as HTMLElement)?.blur?.();
    }

    // Forzar que el body tenga el foco para limpiar cualquier estado residual
    document.body.focus();

    // Quitar el foco del body inmediatamente para evitar mostrar outline
    setTimeout(() => {
      document.body.blur();
    }, 5);
  }

  private disableFormTemporarily(): void {
    // Crear overlay para bloquear interacciones en lugar de deshabilitar controles
    this.createModalOverlay();
  }

  private createModalOverlay(): void {
    // Remover overlay existente si existe
    this.removeModalOverlay();

    const overlay = document.createElement('div');
    overlay.id = 'insurance-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: transparent;
      z-index: 4000;
      pointer-events: auto;
    `;

    // Agregar evento para prevenir cualquier interacción
    overlay.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    });

    overlay.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    });

    document.body.appendChild(overlay);
  }

  private removeModalOverlay(): void {
    const overlay = document.getElementById('insurance-modal-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  private enableFormAndRestoreState(): void {
    // Remover overlay de bloqueo
    this.removeModalOverlay();

    // Restaurar scroll del body
    if (document.body) {
      document.body.style.overflow = '';
    }

    // Limpiar referencias de foco
    this._focusElement = null;
    this._fieldName = null;
  }

  private resetAllAPIDifferenceSignals() {
    this.hasDownPaymentDifferentFromAPI.set(false);
    this.hasFinancialAmountDifferentFromAPI.set(false);
    this.hasOpeningFeeDifferentFromAPI.set(false);
    this.hasSubsidyDifferentFromAPI.set(false);
    this.hasPurchaseFixedAmountDifferentFromAPI.set(false);
    this.highlightFields = this.financialCalculator.emptyHighlightFields;
  }

  private onlyCalculateDifferenceFinancialAmount() {
    const fiancialAmountResult = this.ctr['result'].value?.financialAmount;
    const financialAmount = this.financialCalculator.calculateFinancialAmount(this.formGroup);
    return this.verifyDifferenceBetweenAPIAndCalculated(
      fiancialAmountResult,
      financialAmount,
      'hasFinancialAmountDifferentFromAPI'
    );
  }

  private onlyCalculateDifferenceOpeningFee() {
    const openingFeeResult = this.ctr['result'].value?.openingfeeAmount;
    const openingFeeCalculated = this.financialCalculator.calculateCommissionForOpening(
      this.formGroup,
      TaxType.invoiceWithTax
    );
    return this.verifyDifferenceBetweenAPIAndCalculated(
      openingFeeResult,
      openingFeeCalculated,
      'hasOpeningFeeDifferentFromAPI'
    );
  }

  private onlyCalculateDifferenceSubsidy() {
    const type = this.ctr['subsidyType'].value;
    if (type != TaxType.amountToFinance) {
      return false;
    }
    const financialAmount = this.ctr['result'].value?.financialAmount;
    const financialAmountCalculated = this.financialCalculator.calculateFinancialAmount(this.formGroup);

    // Obtener el porcentaje del subsidio
    const subsidyPercentage = this.ctr['subsidyPercentage'].value;
    if (!subsidyPercentage) {
      return false;
    }

    // Calcular subsidio basado en monto a financiar de la API
    const subsidyApiCalculated = financialAmount ? Number(financialAmount) * (subsidyPercentage / 100) : 0;
    const subsidyCalculated = Number(financialAmountCalculated) * (subsidyPercentage / 100);

    return this.verifyDifferenceBetweenAPIAndCalculated(
      subsidyApiCalculated,
      subsidyCalculated,
      'hasSubsidyDifferentFromAPI'
    );
  }

  private isInsuranceParamsValid(): boolean {
    const params = this.insuranceService.insuranceParams();
    return !!params && !!params.financialProduct && !!params.companyName;
  }

  private getFormValidationErrors(): string[] {
    const errors: string[] = [];
    Object.keys(this.ctr).forEach((key) => {
      const control = this.formGroup.get(key);
      if (control?.errors) {
        errors.push(key);
      }
    });
    return errors;
  }

  private calculateWarrantyDepositTotal() {
    const warrantyTotal = this.financialCalculator.calculateDepositsInGuarantee(this.formGroup);
    this.ctr['warrantyDepositTotal'].setValue(warrantyTotal);
  }

  private verifyDifferenceBetweenAPIAndCalculated(
    apiValue: any,
    calculatedValue: any,
    fieldSaveDifference: keyof this
  ) {
    const hasAPIResult = apiValue != null && apiValue != undefined;
    if (!hasAPIResult) {
      // Si no hay resultado de API, siempre resetear la diferencia a false
      const field = this[fieldSaveDifference] as { set?: (value: boolean) => void };
      field?.set?.(false);
      return false;
    }
    const apiNum = Number(apiValue);
    const calcNum = Number(calculatedValue);
    if (isNaN(apiNum) || isNaN(calcNum)) {
      const difference = apiValue != calculatedValue;
      const field = this[fieldSaveDifference] as { set?: (value: boolean) => void };
      field?.set?.(difference);
      return difference;
    }
    const absoluteDifference = Math.abs(apiNum - calcNum);
    const tolerance = 1;
    const hasSignificantDifference = absoluteDifference > tolerance;
    const field = this[fieldSaveDifference] as { set?: (value: boolean) => void };
    field?.set?.(hasSignificantDifference);
    return hasSignificantDifference;
  }

  private cleanupOnError() {
    this.removeNullItems('specialPayments');
    this.removeNullItems('grace');
    this.removeNullItems('irregularPayments');
  }

  private handleCalculationError() {
    this.showNotificationError(
      'Ha ocurrido un error al momento de realizar el calculo. Por favor, intente nuevamente o solicite soporte.'
    );
    this.cleanupOnError();
  }

  private buildDeleteOperations(
    payments: any[],
    idDeal: any,
    insurancePayments: any[],
    insuranceId: any
  ): Observable<any>[] {
    const deleteOperations = [];

    if (payments?.length > 0 && idDeal != null) {
      deleteOperations.push(this.deleteOpenQuotePayments(idDeal));
    }

    if (insurancePayments?.length > 0 && idDeal != null && insuranceId != null) {
      deleteOperations.push(this.deleteMarshInsurancePaymentsRefactor(idDeal, insuranceId));
    }

    return deleteOperations;
  }

  private processPaymentsIfNeeded(response: any) {
    const payments = this.ctr['payments'].value;
    const idDeal = this.ctr['id'].value;

    if (payments?.length > 0 && idDeal != null) {
      this.loading.track(this.deleteOpenQuotePayments(idDeal)).subscribe({
        next: () => {
          this.assignPayments(response);
        },
        error: () => this.handleCalculationError(),
      });
    } else {
      this.assignPayments(response);
    }
  }

  private processQuoteCalculation(quoteCalculation: Observable<any>) {
    this.loading.track(quoteCalculation).subscribe({
      next: (response) => {
        if (!this.handleQuoteResponse(response)) {
          return;
        }
        this.processPaymentsIfNeeded(response);
      },
      error: () => this.handleCalculationError(),
    });
  }

  private handleCalculateQuoteAndManualInsurance(data: any, version: string = 'v1') {
    this.deleteManualInsurancePayments().subscribe({
      next: () => {
        this.reCalculateInsuranceDealPayments = false;
        const quoteCalculation = this.quoteService.calculateQuoteOpen(data, version);
        this.processQuoteCalculation(quoteCalculation);
      },
      error: () => this.handleCalculationError(),
    });
  }

  private handleCalculateQuote(data: any, version: string = 'v1') {
    const quoteCalculation = this.quoteService.calculateQuoteOpen(data, version);
    this.processQuoteCalculation(quoteCalculation);
  }

  private handleCalculateQuoteAndMarshInsurance(data: any, insurance: any, version: string = 'v1') {
    const quoteCalculation = this.quoteService.calculateQuoteOpen(data, version);
    const insuranceAmortizations = this.createCalculateInsuranceAmortization(version);

    this.loading.track(forkJoin([quoteCalculation, insuranceAmortizations])).subscribe({
      next: ([quoteResponse, insuranceResponse]) => {
        if (!this.handleQuoteResponse(quoteResponse) || !this.handleMarshInsuranceResponse(insuranceResponse)) {
          return;
        }
        this.processMarshPayments(quoteResponse, insuranceResponse, insurance);
      },
      error: () => this.handleCalculationError(),
    });
  }

  private processMarshPayments(quoteResponse: any, insuranceResponse: any, insurance: any) {
    const payments = this.ctr['payments'].value;
    const idDeal = this.ctr['id'].value;
    const insurancePayments = insurance?.openQuoteInsurancePayments;

    const deleteOperations = this.buildDeleteOperations(payments, idDeal, insurancePayments, insurance?.id);

    if (deleteOperations.length > 0) {
      this.loading.track(forkJoin(deleteOperations)).subscribe({
        next: () => {
          this.assignMarshPayments(insuranceResponse);
          this.assignPayments(quoteResponse);
        },
        error: () => this.handleCalculationError(),
      });
    } else {
      this.assignMarshPayments(insuranceResponse);
      this.assignPayments(quoteResponse);
    }
  }

  private assignMarshPayments(insuranceResponse: any) {
    const insurance = this.ctr['insurance'].value;
    const { insuranceResult, payments } = this.insuranceService.getMappedAmortization(insuranceResponse);
    Object.entries(insuranceResult).forEach(([key, value]) => {
      insurance[key] = value;
    });
    insurance['openQuoteInsurancePayments'] = payments;
    this.ctr['insurance'].setValue(insurance);
  }

  private deleteMarshInsurancePaymentsRefactor(idDeal: number, idInsurance: number) {
    return this.insuranceService.deleteMarshInsurancePayments(this.idQuote, idDeal, idInsurance).pipe(take(1));
  }

  private createCalculateInsuranceAmortization(version: string = 'v1') {
    const insurance = this.ctr['insurance'].value;
    let model: any = this.financialCalculator.getBodyforCalculateInsuranceMarsh(
      insurance,
      this.insuranceService.insuranceParams()!
    );
    return this.insuranceService.createInsuranceAmortization(model, version);
  }

  private handleQuoteResponse(response: any): boolean {
    let status = true;
    if (!response.status && response.error === Constants.QUOTE_ERROR_PROCOTIZA_TIME_OUT_INTEGRATION) {
      this.showNotificationError(this.translateService.instant(Constants.QUOTE_ERROR_PROCOTIZA_TIME_OUT_INTEGRATION));
      status = false;
    } else if (!response.status && response.error && response.error.Detalle) {
      this.showNotificationError(response.error.Detalle);
      status = false;
    } else if (!response.status) {
      this.showNotificationError(
        'Ha ocurrido un error al momento de realizar el calculo. Por favor, intente nuevamente o solicite soporte.'
      );
      status = false;
    }
    if (!status) {
      this.cleanupOnError();
    }
    return status;
  }

  private handleMarshInsuranceResponse(insuranceResponse: any): boolean {
    if (insuranceResponse.error) {
      const message = insuranceResponse.needTranslate
        ? this.translateService.instant(insuranceResponse.message)
        : insuranceResponse.message;
      this.showNotificationError(message);
      return false;
    }

    return true;
  }

  private cleanModelCalculatedData(model: any): any {
    const data = JSON.parse(JSON.stringify(model));
    Object.keys(data).forEach((key) => {
      if (
        this.financialCalculator.isInvalidNumericPayload(data[key]) ||
        (data[key] !== 0 && !data[key])
      ) {
        delete data[key];
      }
    });
    return data;
  }

  private removeNullItems(controlName: string) {
    const items = this.ctr[controlName].value;
    if (items.length === 0) return;
    this.ctr[controlName].setValue(items.filter((item: any) => item.id != null));
  }

  private validateDownPaymentConfiguration(type: string): {
    isValid: boolean;
    fieldsRequired: string[];
    fieldsRemoveValidators: string[];
  } {
    const isPercentageType = this.isPercentageBasedType(type);
    const isFixedAmount = type === this.taxType.fixedAmount;
    let fieldsRequired: string[] = [];
    let fieldsRemoveValidators: string[] = [];

    if (isPercentageType) {
      fieldsRequired = ['downPaymentTax'];
      fieldsRemoveValidators = ['downPayment'];
      this.configurePercentageBasedValidation();

      if (!this.hasValidDataForCalculation()) {
        return { isValid: false, fieldsRequired, fieldsRemoveValidators };
      }
    } else if (isFixedAmount) {
      this.handleFixedAmountDownPayment();
      return { isValid: true, fieldsRequired, fieldsRemoveValidators };
    } else {
      this.ctr['downPaymentTax'].setValue(null);
    }

    return { isValid: true, fieldsRequired, fieldsRemoveValidators };
  }

  private performDownPaymentCalculation(type: string): { calculatedAmount: number; hadFormChanges: boolean } {
    const isPercentageType = this.isPercentageBasedType(type);
    const downpaymentCalculated = this.financialCalculator.calculateDownPayment(this.formGroup);

    if (!isPercentageType) {
      const number = Number(downpaymentCalculated);
      return { calculatedAmount: isNaN(number) ? 0 : number, hadFormChanges: true };
    }

    const calculationData = this.getDownPaymentCalculationData();
    const calculatedRounded = Number(downpaymentCalculated) || 0;

    const hadFormChanges = this.detectDownPaymentPercentageChanges(calculationData);

    return { calculatedAmount: calculatedRounded, hadFormChanges };
  }

  private resetDownPaymentFields() {
    const fields = ['downPaymentTax', 'downPayment'];
    this.removeValidators(fields);
    this.setValueAndErrors(fields);
    fields.forEach((field) => {
      this.ctr[field].setValue(null);
    });
  }

  private isPercentageBasedType(type: string): boolean {
    return type === this.taxType.invoiceWithTax || type === this.taxType.invoiceWithoutTax;
  }

  private getDownPaymentCalculationData() {
    return {
      unitPriceWithoutTax: this.ctr['unitPriceWithoutTax'].value,
      downPaymentTax: Number(this.ctr['downPaymentTax'].value),
      tax: Number(this.ctr['tax'].value) / 100,
      financialProduct: this.ctr['financialProductType'].value,
    };
  }

  private configurePercentageBasedValidation(): void {
    this.ctr['downPayment'].disable();
    this.ctr['downPayment'].setValue(null);
    this.ctr['downPayment'].clearValidators();
    this.ctr['downPaymentTax'].enable();

    if (
      this.ctr['downPaymentTax'].value == null ||
      this.ctr['downPaymentTax'].value == undefined
    ) {
      this.ctr['downPaymentTax'].setValidators([
        Validators.required,
        Validators.pattern(/^([0-9]{1,2}(\.[0-9]{1,4})?)$/),
      ]);
    }
    this.formGroup.get('downPaymentTax')?.updateValueAndValidity();
    this.formGroup.get('downPayment')?.updateValueAndValidity();
  }

  /**
   * Verifica si hay datos válidos para realizar el cálculo
   */
  private hasValidDataForCalculation(): boolean {
    const unitPriceWithoutTax = this.ctr['unitPriceWithoutTax'].value;
    const downPaymentTax = this.ctr['downPaymentTax'].value;

    return !(
      unitPriceWithoutTax == null ||
      unitPriceWithoutTax == undefined ||
      downPaymentTax == null ||
      downPaymentTax == undefined
    );
  }

  /**
   * Detecta si hubo cambios en el formulario comparado con el estado anterior
   */
  private detectDownPaymentPercentageChanges(calculationData: any): boolean {
    const originalDownPayment = Number(this.previousFormGroup?.['downPaymentTax']);
    const originalDownPaymentTaxType = this.previousFormGroup?.['downPaymentTaxType'];
    const currentType = this.ctr['downPaymentTaxType'].value;

    return originalDownPayment != calculationData.downPaymentTax || originalDownPaymentTaxType != currentType;
  }

  /**
   * Aplica el resultado del cálculo al formulario
   */
  private applyDownPaymentResult(calculationResult: { calculatedAmount: number; hadFormChanges: boolean }): void {
    const { calculatedAmount, hadFormChanges } = calculationResult;

    if (hadFormChanges) {
      this.handleFormChanges(calculatedAmount);
    } else {
      this.handleNoFormChanges(calculatedAmount);
    }
  }

  /**
   * Maneja el caso cuando hay cambios en el formulario
   */
  private handleFormChanges(calculatedAmount: number): void {
    this.resetAllAPIDifferenceSignals();
    this.ctr['result'].setValue(null);
    this.ctr['downPayment'].setValue(calculatedAmount);
  }

  /**
   * Maneja el caso cuando no hay cambios en el formulario
   */
  private handleNoFormChanges(calculatedAmount: number): void {
    const downpaymentResult = this.ctr['result'].value?.downPayment;
    this.verifyDifferenceBetweenAPIAndCalculated(downpaymentResult, calculatedAmount, 'hasDownPaymentDifferentFromAPI');

    const hasAPIResult = downpaymentResult != null && downpaymentResult != undefined;
    this.ctr['downPayment'].setValue(hasAPIResult ? downpaymentResult : calculatedAmount);
  }

  private executeDownPaymentPostProcessing(hadFormChanges: boolean, validationResult: any): void {
    if (hadFormChanges) {
      this.setFinancialAmountCalculation();
      this.performCommissionCalculationForOpening();
      this.calculateSubsidyOptions();
      this.calculatePurchaseOption();
      this.calculateResidualValueOption();
    }
    this.updateLastChangesForFormGroup();
    this.setValidators(validationResult.fieldsRequired);
    this.removeValidators(validationResult.fieldsRemoveValidators);
    this.formGroup.updateValueAndValidity({ onlySelf: true });
  }

  private handleFixedAmountDownPayment() {
    this.setValidators(['downPayment']);
    this.ctr['downPayment'].setValidators([Validators.required, Validators.min(0)]);
    this.removeValidators(['downPaymentTax']);

    this.ctr['downPaymentTax'].disable();
    this.ctr['downPaymentTax'].setValue(null);
    this.ctr['downPayment'].enable();
  }

  /**
   * Calcula las comisiones de apertura puras sin efectos secundarios
   */
  private calculatePureOpeningFees(): { withoutTax: any; withTax: any } {
    const openingFeeWithoutTax = this.financialCalculator.calculateCommissionForOpening(
      this.formGroup,
      TaxType.invoiceWithoutTax
    );

    const openingFeeWithTax = this.financialCalculator.calculateCommissionForOpening(
      this.formGroup,
      TaxType.invoiceWithTax
    );

    return { withoutTax: openingFeeWithoutTax, withTax: openingFeeWithTax };
  }

  /**
   * Determina qué valores de comisión de apertura usar basado en API y validación
   */
  private determineOpeningFeeValues(
    calculatedFees: { withoutTax: number; withTax: number },
    validated: boolean
  ): { finalWithoutTax: number; finalWithTax: number } {
    if (validated) {
      return {
        finalWithoutTax: calculatedFees.withoutTax,
        finalWithTax: calculatedFees.withTax,
      };
    }

    const openingFeeResult = this.ctr['result'].value?.openingfeeAmount;
    this.verifyDifferenceBetweenAPIAndCalculated(
      openingFeeResult,
      calculatedFees.withTax,
      'hasOpeningFeeDifferentFromAPI'
    );
    const hasAPIResult = openingFeeResult != null && openingFeeResult != undefined;

    if (hasAPIResult) {
      // Siempre usar valor de API cuando está disponible
      const taxDecimal = Number(parseFloat(this.ctr['tax'].value) / 100);
      const openingFeeResultWithoutTax = openingFeeResult / (1 + taxDecimal);
      return {
        finalWithoutTax: openingFeeResultWithoutTax,
        finalWithTax: openingFeeResult,
      };
    } else {
      // Solo usar valores calculados si no hay resultado de API
      return {
        finalWithoutTax: calculatedFees.withoutTax,
        finalWithTax: calculatedFees.withTax,
      };
    }
  }

  private updateOpeningFeeFormControls(fees: { finalWithoutTax: number; finalWithTax: number }): void {
    this.ctr['openingFeeWithoutTax'].setValue(fees.finalWithoutTax);
    this.ctr['openingFeeWithTax'].setValue(fees.finalWithTax);
  }

  private performCommissionCalculationForOpening(validated: boolean = false) {
    this.ctr['openingFeeType'].setValue(TaxType.amountToFinance);
    const calculatedFees = this.calculatePureOpeningFees();
    const finalFees = this.determineOpeningFeeValues(calculatedFees, validated);
    this.updateOpeningFeeFormControls(finalFees);
  }

  // # END PRIVATES

  // INICIO getters para el HTML

  get modelSubBrandName(): string {
    return this._selectedModel?.subBrand?.name;
  }

  get modelBrandName(): string {
    return this._selectedModel?.subBrand?.brand?.name;
  }
  get ctr() {
    return this.formGroup.controls;
  }
  get isActiveBrokerCatalog(): boolean {
    return this.ctr['includesInsurance']?.value;
  }

  get formInsurance() {
    return this.ctr['insurance'].value;
  }
  get insurancePayments() {
    return this.formInsurance?.openQuoteInsurancePayments || [];
  }
  //netInterestMarginEffective && capitalAdvantageNominal
  get formProfitDeal() {
    return this.ctr['profitDeal'].value;
  }

  // Getters logicos
  get isVatExemptVisible(): boolean {
    return (
      this.nameCompany() === 'SOFOM' ||
      this.ctr['financialProductType'].value === FinancialProductEnum.directCredit ||
      this.ctr['financialProductType'].value === FinancialProductEnum.directCreditGlobe
    );
  }
  get noVatExemptVisible(): boolean {
    return (
      this.nameCompany() !== 'SOFOM' ||
      (this.ctr['financialProductType'].value !== FinancialProductEnum.directCredit &&
        this.ctr['financialProductType'].value !== FinancialProductEnum.directCreditGlobe)
    );
  }

  get isViewDownPayment() {
    return (
      this.ctr['financialProductType'].value == FinancialProductEnum.directCredit ||
      this.ctr['financialProductType'].value == FinancialProductEnum.financialLeasing ||
      this.ctr['financialProductType'].value == FinancialProductEnum.capLoan ||
      this.ctr['financialProductType'].value == FinancialProductEnum.directCreditGlobe ||
      this.ctr['financialProductType'].value == FinancialProductEnum.realEstate
    );
  }

  get isFinancialProductCapLoanOrRealEstate() {
    return (
      this.ctr['financialProductType'].value == FinancialProductEnum.capLoan ||
      this.ctr['financialProductType'].value == FinancialProductEnum.realEstate
    );
  }

  get isUsedClassification() {
    return this.ctr['classification'].value === 'USED';
  }

  get isClassificationVoid() {
    return this.ctr['classification'].value === '';
  }

  get isDownPaymentTypeVariable() {
    return this.ctr['downPaymentTaxType'].value != TaxType.fixedAmount;
  }

  get isOpeningFeeTypeVariable() {
    return this.ctr['openingFeeType'].value != TaxType.fixedAmount;
  }
  get isOpeningFeeTypeNotNull() {
    return this.ctr['openingFeeType'].value !== null;
  }
  get isWarrantyDepositNotFixedOrRentBased() {
    const deposit = this.ctr['warrantyDeposit'].value;
    return (
      deposit !== null &&
      deposit !== undefined &&
      deposit !== '' &&
      deposit !== 'null' &&
      deposit !== TaxType.numberOfRents &&
      deposit !== TaxType.fixedAmount
    );
  }

  get warrantyDepositViewTotal() {
    return this.ctr['warrantyDeposit'].value &&
      ((this.ctr['warrantyDepositTotal'].value != null || this.ctr['warrantyDepositTotal'].value != undefined) &&
        this.ctr['warrantyDeposit'].value === TaxType.fixedAmount)
  }

  get isWarrantyDepositRentBased() {
    return this.ctr['warrantyDeposit'].value == TaxType.numberOfRents;
  }
  get isWarrantyDepositNotFixedAmount() {
    return this.ctr['warrantyDeposit']?.value != TaxType.fixedAmount;
  }
  get isSubsidyTypeNotNull(): boolean {
    return Boolean(this.ctr['subsidyType'].value);
  }

  get isSubsidyTypeNotFixedAmount(): boolean {
    return this.ctr['subsidyType'].value != TaxType.fixedAmount;
  }
  get isFinancialProductCreditGlobe() {
    return this.ctr['financialProductType'].value == FinancialProductEnum.directCreditGlobe;
  }

  get hasCalculatedResult() {
    return !!this.ctr['result'].value;
  }

  get isDisabledCalculateQuote() {
    // Si existe un resultado (ya se calculó), bloquear el botón
    if (this.ctr['result'].value) {
      return true;
    }

    // Para otros productos, usar la validación general
    return !this.formGroup.valid || this.validationsService.isValidFinancialProduct(this.formGroup);
  }

    get isDisabledCalculateQuoteV2() {
    // Para otros productos, usar la validación general
    return !this.formGroup.valid || this.validationsService.isValidFinancialProduct(this.formGroup);
  }

  get isDisableEditPayments() {
    return !this.formGroup.valid || this.validationsService.isValidFinancialProduct(this.formGroup);
  }

  get isDisabledCalculateQuoteToDownloadJson() {
    if (this.isCapLoanOrRealEstate()) {
      return !this.validCapLoanOrRealEstate();
    }

    return !this.formGroup.valid || this.validationsService.isValidFinancialProduct(this.formGroup);
  }
  get isAvailableInsurancePayments() {
    return this.ctr['insurance'].value?.openQuoteInsurancePayments?.length > 0;
  }

  get isInsuranceCalculated() {
    return this.ctr['insurance'].value?.packageList != null;
  }

  get hasInsuranceNotCalculated() {
    return this.ctr['insurance'].value !== null && !this.isInsuranceCalculated;
  }

  get hasManualInsurance() {
    return (
      this.ctr['insuranceDeals'].value != null &&
      this.ctr['insuranceDeals']?.value?.length > 0
    );
  }

  get isSubsidyTypeNotEmpty(): boolean {
    const subsidyType: any = this.ctr['subsidyType'].value;
    return subsidyType != '' && subsidyType != 'null' && subsidyType != null && subsidyType != undefined;
  }

  get isBalloonTypeNotEmpty(): boolean {
    const balloonType: any = this.ctr['balloonType'].value;
    return balloonType != '' && balloonType != 'null' && balloonType != null && balloonType != undefined;
  }

  get isWarrantyDepositTotalNotReadonly() {
    const deposit = this.ctr['warrantyDeposit'].value;
    return (
      this.ctr['warrantyDeposit'].value &&
      (this.ctr['warrantyDepositTotal'].value != null || this.ctr['warrantyDeposit'].value === TaxType.fixedAmount)
    );
  }
  get isAccesoriesProduct() {
    return this.ctr['productType'].value === productTypeEnum.accesorios;
  }

  get isWarrantyDepositFixedAmount() {
    return this.ctr['warrantyDeposit'].value === TaxType.fixedAmount;
  }
  get isBalloonTypeNotNull(): boolean {
    return Boolean(this.ctr['balloonType'].value);
  }
  get isBalloonTypeNotFixedAmount(): boolean {
    return this.ctr['balloonType'].value != TaxType.fixedAmount;
  }
  get isResidualValueTypeNotNull(): boolean {
    return Boolean(this.ctr['residualValueType'].value);
  }
  get isResidualValueTypeNotFixedAmount(): boolean {
    return this.ctr['residualValueType'].value != TaxType.fixedAmount;
  }
  get isPurchaseOptionTypeNotNull(): boolean {
    return Boolean(this.ctr['purchaseOptionType'].value);
  }
  get isPurchaseOptionTypeNotFixedAmount(): boolean {
    return this.ctr['purchaseOptionType'].value != TaxType.fixedAmount;
  }
  get isPurchaseOptionTypeAmountToFinance(): boolean {
    return this.ctr['purchaseOptionType'].value == TaxType.amountToFinance;
  }
  get isFinancialProductTypeFinancialLeasing(): boolean {
    return this.ctr['financialProductType'].value == FinancialProductEnum.financialLeasing;
  }
  get isFinancialProductTypePureLease(): boolean {
    return this.ctr['financialProductType'].value == FinancialProductEnum.pureLease;
  }
  get isFinancialProductTypeDirectCredit(): boolean {
    return (
      this.ctr['financialProductType'].value == FinancialProductEnum.directCredit ||
      this.ctr['financialProductType'].value == FinancialProductEnum.directCreditGlobe
    );
  }

  get purchaseOptionAmountToFinanceHasDifference(): boolean {
    return (
      this.ctr['purchaseOptionType'].value == TaxType.amountToFinance &&
      this.hasFinancialAmountDifferentFromAPI()
    );
  }
  get purchaseOptionFixedAmountHasDifference(): boolean {
    return (
      this.ctr['purchaseOptionType'].value == TaxType.fixedAmount &&
      this.hasPurchaseFixedAmountDifferentFromAPI()
    );
  }
  get purchaseOptionHasDifference(): boolean {
    return (
      this.purchaseOptionAmountToFinanceHasDifference ||
      this.purchaseOptionFixedAmountHasDifference
    );
  }
  get subsidyAmountToFinanceHasDifference(): boolean {
    return this.ctr['subsidyType'].value == TaxType.amountToFinance && this.hasFinancialAmountDifferentFromAPI();
  }
  get hasBallonFields() {
    return (
      this.ctr['financialProductType'].value == FinancialProductEnum.directCreditGlobe ||
      this.ctr['financialProductType'].value == FinancialProductEnum.capLoan ||
      this.ctr['financialProductType'].value == FinancialProductEnum.realEstate ||
      (this.ctr['financialProductType'].value == FinancialProductEnum.financialLeasing && this.isActiveBallon)
    );
  }
  get isBallonTypeNotNull() {
    return this.ctr['balloonType'].value !== null && this.ctr['balloonType'].value !== undefined && this.ctr['balloonType'].value !== '';
  }
  get isBallonTypeFixedAmount() {
    return this.ctr['balloonType'].value === TaxType.fixedAmount && this.isBallonTypeNotNull;
  }

  get isNormalCreditOrCreditGlobe() {
    return (
      this.ctr['financialProductType'].value == FinancialProductEnum.directCredit ||
      this.ctr['financialProductType'].value == FinancialProductEnum.directCreditGlobe
    );
  }
  get maxDownPaymentValue() {
    const fnProductType = this.ctr['financialProductType'].value;
    return fnProductType === FinancialProductEnum.pureLease ? this.ctr['unitPriceWithoutTax'].value - 1 : this.ctr['unitPriceWithTax'].value - 1;
  }

  // Style getters
  get purchaseTotalWithoutTaxStyle(): any {
    // Monto fijo NO pinta este lado: el Sin IVA es el input tipeado
    // por el usuario; el valor discrepante es el Con IVA de la API.
    return {
      color: this.purchaseOptionAmountToFinanceHasDifference ? 'red' : 'inherit',
      'font-weight': this.highlightFields.purchaseWithoutTax ? 'bold' : 'normal',
    };
  }
  get purchaseTotalWithTaxStyle(): any {
    return {
      color: this.purchaseOptionHasDifference ? 'red' : 'inherit',
      'font-weight': this.highlightFields.purchaseOptionWithTax ? 'bold' : 'normal',
    };
  }
  get subsidyTotalWithoutTaxStyle(): any {
    return { color: this.hasSubsidyDifferentFromAPI() ? 'red' : 'inherit', 'font-weight': 'normal' };
  }
  get balloonTotalWithoutTaxStyle(): any {
    return { 'font-weight': this.highlightFields.balloonWithoutTax ? 'bold' : 'normal' };
  }
  get balloonTotalWithTaxStyle(): any {
    return { 'font-weight': this.highlightFields.balloonWithTax ? 'bold' : 'normal' };
  }
  get subsidyTotalWithTaxStyle(): any {
    return { color: this.hasSubsidyDifferentFromAPI() ? 'red' : 'inherit', 'font-weight': 'normal' };
  }
  get residualValueTotalWithoutTaxStyle(): any {
    return { 'font-weight': this.highlightFields.residualValueWithoutTax ? 'bold' : 'normal' };
  }
  get residualValueTotalWithTaxStyle(): any {
    return { 'font-weight': this.highlightFields.residualValueWithTax ? 'bold' : 'normal' };
  }
  get openingFeeWithoutTaxStyle(): any {
    return {
      color: this.hasOpeningFeeDifferentFromAPI() ? 'red' : 'inherit',
      'font-weight': this.highlightFields.openingFeeWithoutTax ? 'bold' : 'normal',
    };
  }
  get openingFeeWithTaxStyle(): any {
    return {
      color: this.hasOpeningFeeDifferentFromAPI() ? 'red' : 'inherit',
      'font-weight': this.highlightFields.openingFeeWithTax ? 'bold' : 'normal',
    };
  }
  get financialAmountStyle(): any {
    return { color: this.hasFinancialAmountDifferentFromAPI() ? 'red' : 'inherit' };
  }

  getAutoFieldResidualValueTotal(id: 'withTax' | 'withoutTax') {
    const { withTax, withoutTax } = this.financialCalculator.getResidualFields(this.ctr['residualValueType'].value);
    if (id == 'withTax') {
      return this.ctr?.[withTax]?.value;
    }
    return this.ctr?.[withoutTax]?.value;
  }

  getAutoFieldSubsidyTotal(id: 'withTax' | 'withoutTax') {
    const { withTax, withoutTax } = this.financialCalculator.getSubsidyFields(this.ctr['subsidyType'].value);
    if (id == 'withTax') {
      return this.ctr?.[withTax]?.value;
    }
    return this.ctr?.[withoutTax]?.value;
  }
  getAutoFieldPurchaseTotal(id: 'withTax' | 'withoutTax') {
    const { withTax, withoutTax } = this.financialCalculator.getPurchaseFields(this.ctr['purchaseOptionType'].value);
    if (id == 'withTax') {
      return this.ctr?.[withTax]?.value;
    }
    return this.ctr?.[withoutTax]?.value;
  }

  getAutoFieldBalloonTotal(id: 'withTax' | 'withoutTax') {
    const { withTax, withoutTax } = this.financialCalculator.getBalloonFields(this.ctr['balloonType'].value);
    if (id == 'withTax') {
      return this.ctr?.[withTax]?.value;
    }
    return this.ctr?.[withoutTax]?.value;
  }
  // FIN Getters
}
